import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import axios from 'axios';
import { getProfilePicUrl, getLocalProfilePicUrl } from '../config/imageUrls';
import {
  Heart, UserPlus, Eye, MessageCircle, Clock,
  Play, Square, Zap, Users, Mail,
  Activity, AlertCircle, UserMinus, Search as SearchIcon, Settings, Target,
  Check, Plus, X, Hash, ChevronDown, ChevronUp, CheckSquare, Send,
  UserCheck, Scan, BookOpen, Star, List, FileText, Save, ThumbsUp, Link
} from 'lucide-react';
import AutomationBanner from '../components/AutomationBanner';

/* ═══════════════════════════════════════════════
   STYLED COMPONENTS
   ═══════════════════════════════════════════════ */

// Script type → color mapping
const SCRIPT_COLORS = {
  interact: { main: '#22c55e', bg: 'rgba(34,197,94,', rgb: '34,197,94' },
  scrape:   { main: '#3b82f6', bg: 'rgba(59,130,246,', rgb: '59,130,246' },
  unfollow: { main: '#f59e0b', bg: 'rgba(245,158,11,', rgb: '245,158,11' },
  dm:       { main: '#a855f7', bg: 'rgba(168,85,247,', rgb: '168,85,247' },
  'follower-analysis': { main: '#ec4899', bg: 'rgba(236,72,153,', rgb: '236,72,153' },
  'like-comment': { main: '#f43f5e', bg: 'rgba(244,63,94,', rgb: '244,63,94' },
  'interact-post-likers': { main: '#e11d48', bg: 'rgba(225,29,72,', rgb: '225,29,72' },
  'interact-list': { main: '#06b6d4', bg: 'rgba(6,182,212,', rgb: '6,182,212' },
};
const getScriptColor = (type) => SCRIPT_COLORS[type] || SCRIPT_COLORS.interact;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: .5; }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(-8px); }
  to { opacity: 1; transform: translateX(0); }
`;

const Page = styled.div`
  width: 100%;
`;

/* ── Script Type Selector ── */
const ScriptSelector = styled.div`
  display: flex; gap: 6px; align-items: center; flex-wrap: wrap;
`;

const ScriptTypeBtn = styled.button`
  padding: 8px 16px; border-radius: 8px; font-weight: 700; font-size: 11px;
  cursor: pointer; display: flex; align-items: center; gap: 5px; transition: all .15s;
  border: 2px solid transparent;
  background: ${p => {
    const colors = { interact: '34,197,94', scrape: '59,130,246', unfollow: '245,158,11', dm: '139,92,246', detect: '14,165,233', 'follower-analysis': '236,72,153', 'like-comment': '244,63,94' };
    const c = colors[p.$variant] || '165,145,239';
    return p.$selected ? `rgba(${c},.2)` : `rgba(${c},.06)`;
  }};
  color: ${p => {
    const colors = { interact: '#22c55e', scrape: '#3b82f6', unfollow: '#f59e0b', dm: '#8b5cf6', detect: '#0ea5e9', 'follower-analysis': '#ec4899', 'like-comment': '#f43f5e' };
    return colors[p.$variant] || 'var(--text-muted)';
  }};
  border-color: ${p => {
    if (!p.$selected) return 'transparent';
    const colors = { interact: 'rgba(34,197,94,.5)', scrape: 'rgba(59,130,246,.5)', unfollow: 'rgba(245,158,11,.5)', dm: 'rgba(139,92,246,.5)', detect: 'rgba(14,165,233,.5)', 'follower-analysis': 'rgba(236,72,153,.5)', 'like-comment': 'rgba(244,63,94,.5)' };
    return colors[p.$variant] || 'transparent';
  }};
  opacity: ${p => p.$selected ? 1 : .6};
  &:hover { opacity: 1; transform: translateY(-1px); }
  &:disabled { opacity: .3; cursor: not-allowed; transform: none; }
`;

const LaunchBtn = styled.button`
  padding: 8px 20px; border-radius: 8px; border: none;
  background: ${p => {
    const colors = { interact: '#22c55e', scrape: '#3b82f6', unfollow: '#f59e0b', dm: '#8b5cf6', detect: '#0ea5e9', 'follower-analysis': '#ec4899', 'like-comment': '#f43f5e' };
    return colors[p.$variant] || 'var(--primary)';
  }};
  color: #fff; font-weight: 700; font-size: 12px;
  cursor: pointer; display: flex; align-items: center; gap: 6px; transition: all .15s;
  &:hover { opacity: .9; transform: translateY(-1px); }
  &:disabled { opacity: .4; cursor: not-allowed; transform: none; }
`;

const ContentGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 20px;
  @media (max-width: 1100px) { grid-template-columns: 1fr; }
`;

const LeftCol = styled.div`
  display: flex; flex-direction: column; gap: 14px;
  min-width: 0;
`;

/* ── Session Info Card ── */
const SessionCard = styled.div`
  padding: 14px 18px; border-radius: 12px;
  background: ${p => `linear-gradient(135deg, ${p.$sc?.bg || 'rgba(34,197,94,'}0.08), ${p.$sc?.bg || 'rgba(34,197,94,'}0.02))`};
  border: 1px solid ${p => `${p.$sc?.bg || 'rgba(34,197,94,'}0.2)`};
  display: flex; align-items: center; gap: 14px;
`;

const SessionDot = styled.div`
  width: 10px; height: 10px; border-radius: 50%;
  background: ${p => p.$sc?.main || 'var(--success)'};
  animation: ${pulse} 1.5s infinite;
  flex-shrink: 0;
`;

const SessionInfo = styled.div`
  flex: 1;
  .session-type { font-size: 13px; font-weight: 700; color: var(--text-primary); text-transform: capitalize; }
  .session-meta { font-size: 11px; color: var(--text-muted); margin-top: 1px; }
`;

const SessionTimer = styled.div`
  font-size: 20px; font-weight: 800; color: ${p => p.$sc?.main || 'var(--success)'};
  font-family: 'JetBrains Mono', monospace;
`;

/* ── Account Card ── */
const AccountCard = styled.div`
  padding: 18px 20px; border-radius: 12px;
  background: var(--card-bg); border: 1px solid var(--card-border);
`;

const AccountCardGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 0;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 14px;
  }
`;

const AccountLeft = styled.div`
  display: flex; align-items: center; gap: 14px;
  min-width: 0;
`;

const AccountRight = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px; align-content: center; justify-content: flex-end;
  padding-left: 14px; border-left: 1px solid var(--border);
  @media (max-width: 768px) {
    padding-left: 0;
    border-left: none;
    padding-top: 14px;
    border-top: 1px solid var(--border);
    justify-content: flex-start;
  }
`;

const DayStat = styled.div`
  display: flex; align-items: center; gap: 6px; width: calc(33.33% - 4px);
  padding: 4px 8px; border-radius: 8px;
  background: ${p => p.$bg || 'rgba(165,145,239,.06)'};
  .ds-val { font-size: 16px; font-weight: 800; color: ${p => p.$color || 'var(--primary)'}; font-family: 'JetBrains Mono', monospace; }
  .ds-label { font-size: 9px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; }
`;

const AccountAvatar = styled.div`
  width: 56px; height: 56px; border-radius: 50%; background: var(--gradient);
  display: flex; align-items: center; justify-content: center; color: #fff;
  font-weight: 700; font-size: 20px; flex-shrink: 0; overflow: hidden;
  border: 2px solid rgba(165,145,239,.3); position: relative;
  img { width: 100%; height: 100%; object-fit: cover; position: absolute; top: 0; left: 0; border-radius: 50%; }
`;

const AccountName = styled.div`
  font-size: 17px; font-weight: 700; color: var(--text-primary);
  span { font-size: 12px; color: var(--text-muted); font-weight: 500; display: block; margin-top: 2px; }
`;

const AccountLabel = styled.div`
  font-size: 10px; font-weight: 600; color: var(--text-muted); text-transform: uppercase;
  letter-spacing: .5px; margin-bottom: 6px;
`;

const ProgressBarOuter = styled.div`
  width: 100%; height: 8px; border-radius: 4px; background: var(--surface);
  overflow: hidden;
`;

const ProgressBarFill = styled.div`
  height: 100%; border-radius: 4px; transition: width .5s ease;
  background: linear-gradient(90deg, var(--primary), var(--accent));
  width: ${p => p.$pct || 0}%;
`;

/* ── Right: Live Activity Feed ── */
const ActivityPanel = styled.div`
  border-radius: 12px; background: var(--card-bg);
  border: 1px solid var(--card-border); display: flex; flex-direction: column;
  max-height: 700px;
`;

const ActivityHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px; border-bottom: 1px solid var(--border); flex-shrink: 0;
  font-size: 15px; font-weight: 700; color: var(--text-primary);
`;

const ActivityCount = styled.span`
  font-size: 12px; font-weight: 600; color: var(--text-muted);
`;

const ActivityList = styled.div`
  flex: 1; overflow-y: auto; padding: 0;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: var(--border); border-radius: 2px; }
`;

const DaySeparator = styled.div`
  padding: 4px 18px; font-size: 9px; font-weight: 700; text-transform: uppercase;
  letter-spacing: .5px; color: var(--text-muted); background: var(--surface);
  border-top: 1px solid var(--border); border-bottom: 1px solid var(--border);
  position: sticky; top: 0; z-index: 1;
`;

const ActivityItem = styled.div`
  display: flex; align-items: center; gap: 12px;
  padding: 10px 18px; transition: background .1s;
  background: ${p => p.$alt ? 'rgba(165,145,239,.03)' : 'transparent'};
  ${p => p.$isNew ? css`animation: ${slideIn} .4s ease-out;` : ''}
  &:hover { background: var(--surface-hover); }
`;

const ActionIconWrap = styled.div`
  width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: ${p => p.$bg || 'rgba(165,145,239,.1)'};
  color: ${p => p.$color || 'var(--primary)'};
  position: relative;
  overflow: hidden;
  img {
    width: 32px; height: 32px; object-fit: cover; border-radius: 50%;
    position: absolute; inset: 0;
    display: block;
  }
`;

const ActionText = styled.div`
  flex: 1; font-size: 13px; color: var(--text-secondary);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  strong { font-weight: 700; color: var(--text-primary); }
`;

const ActionTime = styled.div`
  font-size: 12px; color: var(--text-muted); font-family: 'JetBrains Mono', monospace;
  font-weight: 600; white-space: nowrap; flex-shrink: 0;
  display: flex; align-items: center;
`;

const EmptyState = styled.div`
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 40px; color: var(--text-muted); text-align: center;
  svg { opacity: .2; margin-bottom: 12px; }
  p { font-size: 13px; }
`;

/* ── Target Selector ── */
const TargetCard = styled.div`
  border-radius: 12px; background: var(--card-bg);
  border: 1px solid var(--card-border); overflow: hidden;
  transition: border-color .2s;
  &:hover { border-color: rgba(165,145,239,.3); }
`;

const TargetHeader = styled.div`
  display: flex; align-items: center; gap: 8px; padding: 12px 16px;
  cursor: pointer; user-select: none;
  font-size: 11px; font-weight: 700; color: var(--text-muted);
  text-transform: uppercase; letter-spacing: .5px;
  &:hover { color: var(--text-secondary); }
`;

const TargetCount = styled.span`
  margin-left: auto; font-size: 11px; font-weight: 700;
  padding: 2px 8px; border-radius: 10px;
  background: ${p => p.$count > 0 ? 'rgba(34,197,94,.15)' : 'rgba(239,68,68,.1)'};
  color: ${p => p.$count > 0 ? 'var(--success)' : 'var(--error)'};
`;

const TargetBody = styled.div`
  padding: 0 16px 14px; display: flex; flex-direction: column; gap: 10px;
`;

const TargetActions = styled.div`
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
`;

const SelectAllBtn = styled.button`
  display: flex; align-items: center; gap: 4px;
  padding: 4px 10px; border-radius: 6px; font-size: 11px; font-weight: 600;
  cursor: pointer; border: 1px solid var(--card-border); transition: all .15s;
  background: ${p => p.$active ? 'rgba(34,197,94,.15)' : 'var(--surface)'};
  color: ${p => p.$active ? 'var(--success)' : 'var(--text-muted)'};
  &:hover { border-color: var(--primary); color: var(--text-primary); }
`;

const TargetList = styled.div`
  display: flex; flex-direction: column; max-height: 300px;
  overflow-y: auto; padding: 0;
  &::-webkit-scrollbar { width: 3px; }
  &::-webkit-scrollbar-thumb { background: var(--border); border-radius: 2px; }
`;

const TargetTableHead = styled.div`
  display: grid; grid-template-columns: 28px 1fr 65px 65px 55px 30px; gap: 6px;
  padding: 4px 10px; font-size: 9px; font-weight: 700; text-transform: uppercase;
  letter-spacing: .4px; color: var(--text-muted); border-bottom: 1px solid var(--border);
  position: sticky; top: 0; background: var(--card-bg); z-index: 1;
`;

const SortHeader = styled.span`
  cursor: pointer; user-select: none; display: flex; align-items: center; gap: 2px;
  &:hover { color: var(--text-primary); }
  ${p => p.$active && 'color: var(--primary);'}
`;

const TargetTableRow = styled.button`
  display: grid; grid-template-columns: 28px 1fr 65px 65px 55px 30px; gap: 6px;
  align-items: center; padding: 5px 10px; border: none; text-align: left;
  cursor: pointer; transition: background .1s; font-size: 12px;
  background: ${p => p.$selected ? 'rgba(165,145,239,.1)' : 'transparent'};
  color: ${p => p.$selected ? 'var(--primary)' : 'var(--text-secondary)'};
  &:hover { background: var(--surface-hover); }
`;

const TargetAvatar = styled.div`
  width: 28px; height: 28px; border-radius: 50%; flex-shrink: 0; overflow: hidden;
  background: var(--gradient); display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 700; color: #fff; position: relative;
  img { width: 100%; height: 100%; object-fit: cover; position: absolute; top: 0; left: 0; border-radius: 50%; }
`;

const TargetNameCell = styled.div`
  font-size: 12px; font-weight: 700; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`;

const TargetNumCell = styled.div`
  font-size: 11px; font-weight: 700; font-family: 'JetBrains Mono', monospace;
  color: var(--text-muted); text-align: right;
`;

const ManualInputRow = styled.div`
  display: flex; gap: 6px; align-items: center;
`;

const ManualInput = styled.input`
  flex: 1; padding: 6px 10px; border-radius: 6px; font-size: 12px;
  background: var(--surface); border: 1px solid var(--card-border);
  color: var(--text-primary); outline: none;
  &:focus { border-color: var(--primary); }
  &::placeholder { color: var(--text-muted); }
`;

const ManualAddBtn = styled.button`
  display: flex; align-items: center; justify-content: center;
  width: 30px; height: 30px; border-radius: 6px; border: none;
  background: var(--primary); color: #fff; cursor: pointer; flex-shrink: 0;
  transition: all .15s;
  &:hover { opacity: .85; }
  &:disabled { opacity: .3; cursor: not-allowed; }
`;

/* ── Param Slider Panel ── */
const ParamSection = styled.div`
  padding: 16px 18px; border-radius: 12px;
  background: var(--card-bg); border: 1px solid var(--card-border);
`;

const ParamSectionHeader = styled.div`
  display: flex; align-items: center; gap: 8px; margin-bottom: ${p => p.$collapsed ? '0' : '10px'};
  font-size: 11px; font-weight: 700; color: var(--text-muted);
  text-transform: uppercase; letter-spacing: .5px;
  cursor: pointer; user-select: none;
  &:hover { color: var(--text-secondary); }
`;

const CompactGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 6px 16px;
`;

const PRow = styled.div`
  margin-bottom: 8px;
  &:last-child { margin-bottom: 0; }
`;

const PLabelRow = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 3px;
`;

const PLabel = styled.span`
  font-size: 11px; font-weight: 600; color: var(--text-secondary);
`;

const PValue = styled.span`
  font-size: 12px; font-weight: 800; color: var(--text-primary);
  font-family: 'JetBrains Mono', monospace;
  min-width: 32px; text-align: right;
`;

const Slider = styled.input.attrs({ type: 'range' })`
  width: 100%; height: 6px; -webkit-appearance: none; appearance: none;
  border-radius: 3px; outline: none; cursor: pointer; margin: 0;
  background: ${p => `linear-gradient(to right, ${p.$color || 'var(--primary)'} 0%, ${p.$color || 'var(--primary)'} ${p.$pct || 0}%, var(--surface) ${p.$pct || 0}%, var(--surface) 100%)`};
  opacity: ${p => p.disabled ? .45 : 1};
  pointer-events: ${p => p.disabled ? 'none' : 'auto'};
  &::-webkit-slider-thumb {
    -webkit-appearance: none; width: 16px; height: 16px;
    border-radius: 50%; background: ${p => p.$color || 'var(--primary)'};
    border: 2px solid var(--card-bg); cursor: pointer;
    box-shadow: 0 1px 4px rgba(0,0,0,.3);
  }
  &::-moz-range-thumb {
    width: 14px; height: 14px; border-radius: 50%;
    background: ${p => p.$color || 'var(--primary)'};
    border: 2px solid var(--card-bg); cursor: pointer;
  }
`;

const RangeSliderRow = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 12px;
`;

/* ── Dual Range Slider ── */
const DualRangeWrap = styled.div`
  position: relative; height: 22px; width: 100%;
  .dr-track { position: absolute; top: 8px; left: 0; right: 0; height: 6px;
    border-radius: 3px; background: var(--surface); }
  .dr-fill { position: absolute; top: 8px; height: 6px; border-radius: 3px;
    background: var(--primary); pointer-events: none; }
  input[type='range'] {
    position: absolute; top: 0; left: 0; width: 100%; height: 22px;
    -webkit-appearance: none; appearance: none; background: transparent;
    pointer-events: none; margin: 0; outline: none;
    &::-webkit-slider-thumb {
      -webkit-appearance: none; width: 16px; height: 16px; border-radius: 50%;
      background: var(--primary); border: 2px solid var(--card-bg);
      box-shadow: 0 1px 4px rgba(0,0,0,.3); cursor: pointer; pointer-events: auto;
    }
    &::-moz-range-thumb {
      width: 14px; height: 14px; border-radius: 50%;
      background: var(--primary); border: 2px solid var(--card-bg); cursor: pointer; pointer-events: auto;
    }
  }
`;

/* ── Sessions Section (below actions) ── */
const SessionsPanel = styled.div`
  border-radius: 12px; background: var(--card-bg);
  border: 1px solid var(--card-border); margin-top: 14px;
`;

const SessionsHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 18px; border-bottom: 1px solid var(--border);
  font-size: 13px; font-weight: 700; color: var(--text-primary);
  cursor: pointer; user-select: none;
  &:hover { color: var(--primary); }
`;

const SessionRow = styled.div`
  display: flex; align-items: center; gap: 12px;
  padding: 10px 18px; border-bottom: 1px solid var(--border);
  &:last-child { border-bottom: none; }
`;

const SessionBarWrap = styled.div`
  flex: 1; min-width: 0;
`;

const SessionMeta = styled.div`
  display: flex; align-items: center; gap: 6px; font-size: 10px; color: var(--text-muted);
  font-weight: 600; white-space: nowrap; flex-shrink: 0;
`;

const SessionBadge = styled.span`
  padding: 2px 8px; border-radius: 10px; font-size: 10px; font-weight: 700;
  font-family: 'JetBrains Mono', monospace;
  background: ${p => p.$bg || 'rgba(165,145,239,.1)'};
  color: ${p => p.$color || 'var(--primary)'};
`;

const MethodGrid = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px;
`;

const MethodOption = styled.button`
  padding: 7px 14px; border-radius: 8px; font-size: 11px; font-weight: 600;
  cursor: pointer; border: 1.5px solid transparent; transition: all .15s;
  background: ${p => p.$active ? 'rgba(165,145,239,.2)' : 'var(--surface)'};
  color: ${p => p.$active ? 'var(--primary)' : 'var(--text-muted)'};
  border-color: ${p => p.$active ? 'rgba(165,145,239,.5)' : 'var(--card-border)'};
  &:hover { border-color: var(--primary); color: var(--text-primary); }
  &:disabled { opacity: .4; cursor: not-allowed; }
`;

const ToggleRow = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 0; border-bottom: 1px solid var(--border);
  &:last-of-type { border-bottom: none; }
`;

const ToggleLabel = styled.span`
  font-size: 12px; font-weight: 600; color: var(--text-secondary);
`;

const Toggle = styled.div`
  width: 38px; height: 20px; border-radius: 10px; cursor: pointer;
  background: ${p => p.$on ? 'var(--primary)' : 'var(--surface)'};
  border: 1px solid ${p => p.$on ? 'var(--primary)' : 'var(--card-border)'};
  position: relative; transition: all .2s; flex-shrink: 0;
  opacity: ${p => p.$disabled ? .45 : 1};
  pointer-events: ${p => p.$disabled ? 'none' : 'auto'};
  &::after {
    content: ''; position: absolute; top: 2px;
    left: ${p => p.$on ? '19px' : '2px'};
    width: 14px; height: 14px; border-radius: 50%;
    background: ${p => p.$on ? '#fff' : 'var(--text-muted)'};
    transition: all .2s;
  }
`;

const DmTextarea = styled.textarea`
  width: 100%; min-height: 60px; padding: 8px 10px; border-radius: 8px;
  font-size: 12px; background: var(--surface); border: 1px solid var(--card-border);
  color: var(--text-primary); outline: none; resize: vertical; font-family: inherit;
  box-sizing: border-box;
  &:focus { border-color: var(--primary); }
  &::placeholder { color: var(--text-muted); }
  &:disabled { opacity: .45; }
`;

/* ═══════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════ */

const ACTION_MAP = {
  'LikeAction': { label: 'Liked post from', icon: Heart, color: '#ef4444', bg: 'rgba(239,68,68,.12)' },
  'FollowAction': { label: 'Followed', icon: UserPlus, color: '#22c55e', bg: 'rgba(34,197,94,.12)' },
  'GetProfileAction': { label: 'Viewed profile', icon: UserCheck, color: '#a591ef', bg: 'rgba(165,145,239,.12)', showProfilePic: true },
  'StoryWatchAction': { label: 'Viewed story from', icon: BookOpen, color: '#f59e0b', bg: 'rgba(245,158,11,.12)' },
  'StoryLikeAction': { label: 'Liked story from', icon: Star, color: '#ec4899', bg: 'rgba(236,72,153,.12)' },
  'UnfollowAction': { label: 'Unfollowed', icon: UserMinus, color: '#f97316', bg: 'rgba(249,115,22,.12)' },
  'CommentAction': { label: 'Commented on', icon: MessageCircle, color: '#3b82f6', bg: 'rgba(59,130,246,.12)' },
  'DirectMessageAction': { label: 'DM to', icon: Send, color: '#8b5cf6', bg: 'rgba(139,92,246,.12)' },
  'ScrapeAction': { label: 'Scraped', icon: Scan, color: '#06b6d4', bg: 'rgba(6,182,212,.12)' },
  // Gmail action types
  'script_start':       { label: 'Script démarré', icon: Play, color: '#0ea5e9', bg: 'rgba(14,165,233,.12)' },
  'script_end':         { label: 'Script terminé', icon: Check, color: '#22c55e', bg: 'rgba(34,197,94,.12)' },
  'input_firstname':    { label: 'Prénom saisi', icon: UserCheck, color: '#8b5cf6', bg: 'rgba(139,92,246,.12)' },
  'input_lastname':     { label: 'Nom saisi', icon: UserCheck, color: '#22c55e', bg: 'rgba(34,197,94,.12)' },
  'input_email':        { label: 'Email saisi', icon: Mail, color: '#0ea5e9', bg: 'rgba(14,165,233,.12)' },
  'input_password':     { label: 'Mot de passe', icon: Settings, color: '#ef4444', bg: 'rgba(239,68,68,.12)' },
  'phone_verification': { label: 'Vérification tél.', icon: AlertCircle, color: '#f59e0b', bg: 'rgba(245,158,11,.12)' },
  'save_db':            { label: 'Sauvegardé en base', icon: Save, color: '#22c55e', bg: 'rgba(34,197,94,.12)' },
  'gmail_action':       { label: 'Action Gmail', icon: Mail, color: '#EA4335', bg: 'rgba(234,67,53,.12)' },
};

const SCRIPT_TYPE_LABELS = { interact: 'Interact', scrape: 'Scrape', unfollow: 'Unfollow', dm: 'DM', detect: 'Détecter', 'follower-analysis': 'Followers', 'like-comment': 'Like Comment', 'interact-post-likers': 'Post Likers', 'interact-list': 'Interact Liste' };

const UNFOLLOW_METHODS = [
  { value: 'database-global-search', label: 'Recherche (barre)' },
  { value: 'database', label: 'Scroll liste (DB)' },
  { value: 'profile', label: 'Scroll liste (profil)' },
  { value: 'list', label: 'Liste fichier' },
];

const FOLLOWING_SORT_OPTIONS = [
  { value: 'latest', label: 'Plus récents' },
  { value: 'earliest', label: 'Plus anciens' },
  { value: 'default', label: 'Ordre par défaut' },
];

const PARAM_DEFS = {
  interact: [
    { key: 'likes_count', label: 'Likes par profil', type: 'range', min: 0, max: 12, defaultVal: '2-4' },
    { key: 'stories_count', label: 'Stories par profil', type: 'range', min: 0, max: 30, defaultVal: '2-4' },
    { key: 'session_length_in_mins_limit', label: 'Durée session (min)', type: 'range', min: 1, max: 480, defaultVal: '240' },
    { key: 'successful_interactions_limit_per_source', label: 'Interactions / source', type: 'range', min: 1, max: 1000 },
    { key: 'follow_percentage', label: 'Follow %', type: 'slider', min: 0, max: 100, unit: '%' },
    { key: 'story_like_percentage', label: 'Story Like %', type: 'slider', min: 0, max: 100, unit: '%' },
    { key: 'comment_percentage', label: 'Comment %', type: 'slider', min: 0, max: 100, unit: '%' },
    { key: 'like_percentage', label: 'Like %', type: 'slider', min: 0, max: 100, unit: '%' },
    { key: 'total_likes_limit', label: 'Limite likes total', type: 'slider', min: 0, max: 1000, step: 5 },
    { key: 'total_story_limit', label: 'Limite stories total', type: 'slider', min: 0, max: 500, step: 5 },
    { key: 'like_target_for_the_day', label: 'Objectif likes / jour', type: 'slider', min: 0, max: 500, step: 5 },
    { key: 'follow_target_for_the_day', label: 'Objectif follows / jour', type: 'slider', min: 0, max: 500, step: 5 },
    { key: 'gemini_analysis', label: 'Analyse Gemini', type: 'toggle', defaultVal: true },
    { key: 'capture_own_profile_pic', label: 'Capturer photo de profil du compte', type: 'toggle', defaultVal: false },
    { key: 'random_tap', label: 'Tap aléatoire (anti-détection)', type: 'toggle', defaultVal: false },
  ],
  unfollow: [
    { key: 'unfollow_type_script', label: "Méthode d'unfollow", type: 'select' },
    { key: 'following_sort_order', label: 'Tri de la liste', type: 'select', options: FOLLOWING_SORT_OPTIONS, showWhen: (getVal) => { const m = getVal('unfollow_type_script'); return m === 'database' || m === 'profile' || m === 'database-global-search'; } },
    { key: 'unfollow_count', label: "Nombre d'unfollows", type: 'range', min: 1, max: 200 },
    { key: 'unfollow_limit', label: 'Limite / session', type: 'slider', min: 1, max: 100 },
    { key: 'unfollow_delay', label: 'Délai entre unfollows (sec)', type: 'range', min: 1, max: 60 },
    { key: 'min_following_hours', label: 'Heures min de suivi', type: 'slider', min: 1, max: 720 },
    { key: 'unfollow_older_than_days', label: 'Plus ancien que (jours)', type: 'slider', min: 1, max: 90 },
    { key: 'session_length_in_mins_limit', label: 'Durée session (min)', type: 'slider', min: 1, max: 480 },
  ],
  scrape: [
    { key: 'total_scrape_limit', label: 'Limite profils total', type: 'range', min: 10, max: 1000, defaultVal: '200-300' },
    { key: 'scrape_limit_per_source', label: 'Limite par source', type: 'range', min: 10, max: 1000, defaultVal: '200-300' },
    { key: 'session_length_in_mins_limit', label: 'Durée session (min)', type: 'slider', min: 1, max: 480, defaultVal: 240 },
    { key: 'gemini_analysis', label: 'Analyse Gemini', type: 'toggle', defaultVal: true },
    { key: 'deep_analysis', label: 'Analyse approfondie (followings)', type: 'toggle', defaultVal: true },
    { key: 'random_tap', label: 'Tap aléatoire (anti-détection)', type: 'toggle', defaultVal: false },
  ],
  dm: [
    { key: 'dm_new_followers', label: 'DM aux nouveaux followers', type: 'toggle' },
    { key: 'dm_to_bot_followed', label: 'DM aux comptes suivis par le bot', type: 'toggle' },
    { key: 'dm_daily_limit', label: 'Limite DMs / jour', type: 'slider', min: 0, max: 100 },
    { key: 'dm_list', label: 'Messages DM (un par ligne)', type: 'text' },
    { key: 'session_length_in_mins_limit', label: 'Durée session (min)', type: 'slider', min: 1, max: 480 },
  ],
  'interact-post-likers': [
    { key: 'likes_count', label: 'Likes par profil', type: 'range', min: 0, max: 12, defaultVal: '2-4' },
    { key: 'stories_count', label: 'Stories par profil', type: 'range', min: 0, max: 30, defaultVal: '2-4' },
    { key: 'session_length_in_mins_limit', label: 'Durée session (min)', type: 'range', min: 1, max: 480, defaultVal: '240' },
    { key: 'successful_interactions_limit_per_source', label: 'Interactions / source', type: 'range', min: 1, max: 1000 },
    { key: 'follow_percentage', label: 'Follow %', type: 'slider', min: 0, max: 100, unit: '%' },
    { key: 'story_like_percentage', label: 'Story Like %', type: 'slider', min: 0, max: 100, unit: '%' },
    { key: 'comment_percentage', label: 'Comment %', type: 'slider', min: 0, max: 100, unit: '%' },
    { key: 'like_percentage', label: 'Like %', type: 'slider', min: 0, max: 100, unit: '%' },
    { key: 'total_likes_limit', label: 'Limite likes total', type: 'slider', min: 0, max: 1000, step: 5 },
    { key: 'total_story_limit', label: 'Limite stories total', type: 'slider', min: 0, max: 500, step: 5 },
    { key: 'gemini_analysis', label: 'Analyse Gemini', type: 'toggle', defaultVal: true },
    { key: 'random_tap', label: 'Tap aléatoire (anti-détection)', type: 'toggle', defaultVal: false },
  ],
  'interact-list': [
    { key: 'likes_count', label: 'Likes par profil', type: 'range', min: 0, max: 12, defaultVal: '2-4' },
    { key: 'stories_count', label: 'Stories par profil', type: 'range', min: 0, max: 30, defaultVal: '2-4' },
    { key: 'session_length_in_mins_limit', label: 'Durée session (min)', type: 'range', min: 1, max: 480, defaultVal: '240' },
    { key: 'follow_percentage', label: 'Follow %', type: 'slider', min: 0, max: 100, unit: '%' },
    { key: 'story_like_percentage', label: 'Story Like %', type: 'slider', min: 0, max: 100, unit: '%' },
    { key: 'comment_percentage', label: 'Comment %', type: 'slider', min: 0, max: 100, unit: '%' },
    { key: 'like_percentage', label: 'Like %', type: 'slider', min: 0, max: 100, unit: '%' },
    { key: 'total_likes_limit', label: 'Limite likes total', type: 'slider', min: 0, max: 1000, step: 5 },
    { key: 'total_story_limit', label: 'Limite stories total', type: 'slider', min: 0, max: 500, step: 5 },
    { key: 'gemini_analysis', label: 'Analyse Gemini', type: 'toggle', defaultVal: true },
    { key: 'random_tap', label: 'Tap aléatoire (anti-détection)', type: 'toggle', defaultVal: false },
  ],
};

const parseRange = (val) => {
  if (val == null) return [0, 0];
  const s = String(val);
  if (s.includes('-')) {
    const parts = s.split('-').map(Number);
    return [parts[0] || 0, parts[1] || parts[0] || 0];
  }
  const n = Number(s) || 0;
  return [n, n];
};

const sliderPct = (val, min, max) => ((val - min) / ((max - min) || 1)) * 100;

const fmtTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Europe/Paris' });
};

const fmtAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60) return 'il y a quelques secondes';
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h${Math.floor((diff % 3600) / 60) > 0 ? Math.floor((diff % 3600) / 60) + 'min' : ''}`;
  return `il y a ${Math.floor(diff / 86400)}j`;
};

const fmtDateLabel = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const actionDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.floor((today - actionDay) / 86400000);
  if (diffDays === 0) return "Aujourd'hui";
  if (diffDays === 1) return 'Hier';
  return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
};

const fmtDuration = (startDate) => {
  if (!startDate) return '00:00:00';
  const diff = Math.floor((Date.now() - new Date(startDate).getTime()) / 1000);
  if (diff < 0) return '00:00:00';
  const h = Math.floor(diff / 3600).toString().padStart(2, '0');
  const m = Math.floor((diff % 3600) / 60).toString().padStart(2, '0');
  const s = (diff % 60).toString().padStart(2, '0');
  return `${h}:${m}:${s}`;
};

/* ═══════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════ */

const AutomationDashboard = ({ selectedDevice, onNavigate, activeTab: currentTab }) => {
  const [actions, setActions] = useState([]);
  const [stats, setStats] = useState({});
  const [processInfo, setProcessInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [launching, setLaunching] = useState(false);
  const [timer, setTimer] = useState('00:00:00');
  const [selectedScript, setSelectedScript] = useState('interact'); // 'interact' | 'scrape' | 'unfollow' | 'dm'
  const [sessionCount, setSessionCount] = useState(0);
  const timerRef = useRef(null);
  const pollRef = useRef(null);
  const prevActionIdsRef = useRef(new Set());
  const stoppedAtRef = useRef(0); // timestamp when stop was clicked — ignore isRunning for 10s

  // Account override: when user switches account via dropdown
  const [pkgOverride, setPkgOverride] = useState(null); // { account_id, username }
  useEffect(() => { setPkgOverride(null); }, [selectedDevice?.account_id]);

  const accountId = pkgOverride?.account_id || selectedDevice?.account_id;
  const deviceId = selectedDevice?.device_id;
  const accountUsername = pkgOverride?.username || selectedDevice?.instagram_username || null;
  const activeAppId = pkgOverride?.app_id || selectedDevice?.app_id || 'com.instagram.android';
  const [cardParams, setCardParams] = useState(null);

  const switchToAccount = (compositeKey) => {
    // compositeKey = "account_id|app_id" e.g. "85|com.instagram.androf"
    const [idStr, ...appParts] = compositeKey.split('|');
    const acctId = parseInt(idStr);
    const appId = appParts.join('|') || null;
    const da = deviceAccounts.find(a => parseInt(a.account_id) === acctId && (a.app_id || 'com.instagram.android') === (appId || 'com.instagram.android'));
    setPkgOverride({ account_id: acctId, username: da?.username || null, app_id: da?.app_id || appId || null });
  };

  // Unified actions: service filter removed — Instagram-only

  const [launchError, setLaunchError] = useState(null);
  const [openTerminal, setOpenTerminal] = useState(true); // checkbox: ouvrir terminal pour logs
  const [todaySessions, setTodaySessions] = useState([]);

  // Target selector state
  const [allTargets, setAllTargets] = useState([]);
  const [selectedTargets, setSelectedTargets] = useState(new Set());
  const [manualTargets] = useState([]);
  const [manualInput, setManualInput] = useState('');
  const [targetsExpanded, setTargetsExpanded] = useState(true);
  const [targetSort, setTargetSort] = useState({ key: 'name', dir: 'asc' }); // key: name | interactions | users
  const [paramsExpanded, setParamsExpanded] = useState(true);
  const [sessionsExpanded, setSessionsExpanded] = useState(true);

  // Scrape-specific state
  const [scrapeTarget, setScrapeTarget] = useState('');
  const [scrapeType, setScrapeType] = useState('followers');
  const [scrapeTargets, setScrapeTargets] = useState([]);
  const [selectedScrapeTargets, setSelectedScrapeTargets] = useState(new Set());

  // Like-comment state
  const [likeCommentPostUrl, setLikeCommentPostUrl] = useState('');
  const [likeCommentText, setLikeCommentText] = useState('');
  // Post-likers state
  const [postLikersUrl, setPostLikersUrl] = useState('');
  const [likeCommentSavedTargets, setLikeCommentSavedTargets] = useState(() => {
    try { return JSON.parse(localStorage.getItem('likeCommentTargets') || '[]'); } catch { return []; }
  });
  const saveLikeCommentTargets = (targets) => {
    setLikeCommentSavedTargets(targets);
    localStorage.setItem('likeCommentTargets', JSON.stringify(targets));
  };

  // Unfollow list management state
  const [showUnfollowListModal, setShowUnfollowListModal] = useState(false);
  const [unfollowListEntries, setUnfollowListEntries] = useState([]);
  const [unfollowListLoading, setUnfollowListLoading] = useState(false);
  const [interactedProfiles, setInteractedProfiles] = useState([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [pickerFilter, setPickerFilter] = useState('follow');
  const [pickerSearch, setPickerSearch] = useState('');
  const [selectedProfiles, setSelectedProfiles] = useState(new Set());
  const [bulkInput, setBulkInput] = useState('');

  // Device accounts (multi-APK package selector)
  const [deviceAccounts, setDeviceAccounts] = useState([]);
  useEffect(() => {
    if (deviceId) {
      axios.get(`/api/instagram-packages/device-accounts/${deviceId}`).then(r => {
        if (r.data?.success) setDeviceAccounts(r.data.accounts || []);
      }).catch(() => setDeviceAccounts([]));
    }
  }, [deviceId]);

  // Custom params overrides (slider edits); reset on device change
  const [customParams, setCustomParams] = useState({});
  useEffect(() => { setCustomParams({}); setScrapeTarget(''); setScrapeType('followers'); }, [accountId, deviceId]);

  const getVal = (key) => {
    if (key in customParams) return customParams[key];
    return cardParams?.[key] ?? null;
  };
  const setParam = (key, val) => setCustomParams(prev => ({ ...prev, [key]: val }));

  // Save current script params to instagram_device_configs
  const [savingParams, setSavingParams] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const saveParams = async () => {
    if (!deviceId) return;
    setSavingParams(true);
    setSaveSuccess(false);
    try {
      const activeScript = selectedScript;
      const defs = PARAM_DEFS[activeScript] || [];
      const body = {};
      for (const def of defs) {
        const v = getVal(def.key);
        if (v != null) body[def.key] = v;
      }
      if (Object.keys(body).length === 0) {
        setSavingParams(false);
        return;
      }
      // Include account_id and app_id so backend targets the correct card row
      body.account_id = accountId;
      body.app_id = activeAppId;
      const res = await fetch(`/api/account-device-cards/${deviceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        setSaveSuccess(true);
        setCustomParams({});
        fetchCardParams();
        setTimeout(() => setSaveSuccess(false), 2000);
      }
    } catch (e) {
      console.error('Save params error:', e);
    }
    setSavingParams(false);
  };

  // Fetch live actions + stats
  const fetchLive = useCallback(async () => {
    if (!accountId) return;
    try {
      const r = await fetch(`/api/live-actions/${accountId}?limit=80`);
      const d = await r.json();
      const newActions = d.actions || [];
      // Mark new actions that weren't in previous fetch
      const prevIds = prevActionIdsRef.current;
      const enriched = newActions.map(a => ({ ...a, _isNew: prevIds.size > 0 && !prevIds.has(a.id) }));
      prevActionIdsRef.current = new Set(newActions.map(a => a.id));
      setActions(enriched);
      setStats(d.stats || {});
    } catch (e) { /* silent */ }
  }, [accountId]);

  // Fetch active process status
  const fetchProcess = useCallback(async () => {
    if (!accountId) return;
    // Skip re-detection for 10s after stop was clicked (prevents race with in-flight polls)
    if (Date.now() - stoppedAtRef.current < 10000) return;
    try {
      const r = await fetch(`/api/script-status/${accountId}`);
      const d = await r.json();
      if (d.isRunning) {
        setProcessInfo(d);
      } else {
        setProcessInfo(null);
      }
    } catch (e) { setProcessInfo(null); }
  }, [accountId]);

  // Fetch instagram_device_configs params (match by account_id + device_id + app_id for correct package row)
  const fetchCardParams = useCallback(async () => {
    if (!accountId || !deviceId) return;
    try {
      const r = await fetch('/api/account-device-cards');
      const d = await r.json();
      // Try exact match with app_id first, fall back to account+device match
      const card = (d || []).find(c => String(c.account_id) === String(accountId) && c.device_id === deviceId && (c.app_id || 'com.instagram.android') === activeAppId)
        || (d || []).find(c => String(c.account_id) === String(accountId) && c.device_id === deviceId);
      setCardParams(card || null);
    } catch (e) { /* silent */ }
  }, [accountId, deviceId, activeAppId]);

  // Fetch interact targets from instagram_targets
  const fetchTargets = useCallback(async () => {
    if (!accountId) return;
    try {
      const r = await fetch(`/api/account-targets/${accountId}?purpose=interact`);
      const d = await r.json();
      const tgts = (d.targets || []).filter(t => t.status === 'active' || !t.status);
      setAllTargets(tgts);
      setSelectedTargets(new Set(tgts.map(t => t.target_profile_name)));
    } catch (e) { /* silent */ }
  }, [accountId]);

  // Fetch scrape targets from instagram_targets
  const fetchScrapeTargets = useCallback(async () => {
    if (!accountId) return;
    try {
      const r = await fetch(`/api/account-targets/${accountId}?purpose=scrape`);
      const d = await r.json();
      const active = (d.targets || []).filter(t => t.status === 'active' || !t.status);
      setScrapeTargets(active);
      // Auto-select ALL scrape targets by default
      setSelectedScrapeTargets(new Set(active.map(t => t.target_profile_name)));
    } catch (e) { /* silent */ }
  }, [accountId]);

  // Fetch session count
  const fetchSessionCount = useCallback(async () => {
    if (!accountId) return;
    try {
      const r = await fetch(`/api/sessions-count/${accountId}`);
      const d = await r.json();
      setSessionCount(d.count || 0);
    } catch (e) { /* silent */ }
  }, [accountId]);

  // Fetch today's sessions with per-session profile counts
  const fetchTodaySessions = useCallback(async () => {
    if (!accountId) return;
    try {
      const r = await fetch(`/api/today-sessions/${accountId}`);
      const d = await r.json();
      setTodaySessions(d.sessions || []);
    } catch (e) { /* silent */ }
  }, [accountId]);

  // Initial load + polling
  useEffect(() => {
    setLoading(true);
    Promise.all([fetchLive(), fetchProcess(), fetchCardParams(), fetchSessionCount(), fetchTargets(), fetchScrapeTargets(), fetchTodaySessions()])
      .finally(() => setLoading(false));
    pollRef.current = setInterval(() => { fetchLive(); fetchProcess(); fetchTodaySessions(); }, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchLive, fetchProcess, fetchCardParams, fetchSessionCount, fetchTargets, fetchScrapeTargets, fetchTodaySessions]);

  // Timer tick — use startedAt from the status response
  useEffect(() => {
    const startTime = processInfo?.startedAt || processInfo?.process?.started_at;
    if (processInfo?.isRunning && startTime) {
      const tick = () => setTimer(fmtDuration(startTime));
      tick();
      timerRef.current = setInterval(tick, 1000);
    } else {
      setTimer('00:00:00');
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [processInfo]);

  // Generic script launcher
  const launchScript = async (type, url, body) => {
    if (launching) return;
    setLaunching(true);
    setLaunchError(null);
    try {
      const r = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await r.json();
      if (!r.ok || data.error) {
        setLaunchError(data.error || `Erreur ${r.status}`);
        setLaunching(false);
        return;
      }
      // Optimistic update: immediately show running state (don't wait for DB poll)
      const startedAt = data.startedAt || new Date().toISOString();
      setProcessInfo({
        isRunning: true,
        running: true,
        scriptType: data.scriptType || type,
        startedAt,
        detectionMethod: 'optimistic',
        process: {
          pid: data.pid || null,
          username: data.username || null,
          device_id: data.device_id || deviceId,
          started_at: startedAt,
          scriptType: data.scriptType || type
        }
      });
      setLaunching(false);
      // Still poll after 5s to get accurate session_id from DB
      setTimeout(() => { fetchProcess(); fetchSessionCount(); }, 5000);
    } catch (e) {
      setLaunchError(`Erreur réseau: ${e.message}`);
      setLaunching(false);
    }
  };

  // ── Unfollow list helpers (DB-backed, persistent across workstations) ──
  const fetchUnfollowList = useCallback(async () => {
    if (!accountId) return;
    setUnfollowListLoading(true);
    try {
      const r = await fetch(`/api/account-lists/unfollow-db/${accountId}`);
      const d = await r.json();
      if (d.success) {
        setUnfollowListEntries(d.entries || []);
      }
    } catch { setUnfollowListEntries([]); }
    setUnfollowListLoading(false);
  }, [accountId]);

  const fetchFollowedProfiles = useCallback(async (filter = 'follow') => {
    if (!accountId) return;
    setProfilesLoading(true);
    try {
      const r = await fetch(`/api/account-lists/${accountId}/interacted-profiles?actionType=${filter}`);
      const d = await r.json();
      if (d.success) setInteractedProfiles(d.profiles || []);
    } catch { setInteractedProfiles([]); }
    setProfilesLoading(false);
  }, [accountId]);

  const openUnfollowListModal = () => {
    setShowUnfollowListModal(true);
    setSelectedProfiles(new Set());
    setPickerSearch('');
    setPickerFilter('follow');
    setBulkInput('');
    fetchUnfollowList();
    fetchFollowedProfiles('follow');
  };

  const addProfilesToUnfollowList = async (usernames, source = 'manual') => {
    if (!accountId || !usernames.length) return;
    try {
      await fetch(`/api/account-lists/unfollow-db/${accountId}/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ usernames, source })
      });
      await fetchUnfollowList();
      setSelectedProfiles(new Set());
      setBulkInput('');
    } catch (e) { console.error('Error adding to unfollow list:', e); }
  };

  const removeFromUnfollowList = async (username) => {
    if (!accountId) return;
    try {
      await fetch(`/api/account-lists/unfollow-db/${accountId}/remove`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ usernames: [username] })
      });
      await fetchUnfollowList();
    } catch (e) { console.error('Error removing from unfollow list:', e); }
  };

  const handleLaunch = () => {
    if (!selectedScript || !deviceId) return;
    // Special handling for detect: no account_id needed
    if (selectedScript === 'detect') {
      launchScript('detect', '/api/instagram/detect-account', { device_id: deviceId, mode: 'terminal' });
      return;
    }
    // Special handling for like-comment
    if (selectedScript === 'like-comment') {
      if (!likeCommentPostUrl || !likeCommentText) {
        setLaunchError('URL du post et texte du commentaire requis');
        setTimeout(() => setLaunchError(null), 3000);
        return;
      }
      const lcBody = {
        device_id: deviceId,
        account_id: accountId,
        post_url: likeCommentPostUrl,
        comment_text: likeCommentText,
        app_id: pkgOverride?.app_id || selectedDevice?.app_id || 'com.instagram.android'
      };
      launchScript('like-comment', '/api/devices/start-like-comment', lcBody);
      return;
    }
    // Special handling for interact-post-likers
    if (selectedScript === 'interact-post-likers') {
      if (!postLikersUrl) {
        setLaunchError('URL du post requis');
        setTimeout(() => setLaunchError(null), 3000);
        return;
      }
      const plDefs = PARAM_DEFS['interact-post-likers'] || [];
      const plParams = {};
      for (const def of plDefs) {
        let v = getVal(def.key);
        if (v == null && def.defaultVal != null) v = def.defaultVal;
        if (v != null) plParams[def.key] = v;
      }
      const plBody = {
        device_id: deviceId,
        account_id: accountId,
        post_url: postLikersUrl,
        app_id: pkgOverride?.app_id || selectedDevice?.app_id || 'com.instagram.android',
        custom_params: Object.keys(plParams).length > 0 ? plParams : undefined
      };
      launchScript('interact-post-likers', '/api/devices/start-interact-post-likers', plBody);
      return;
    }
    // Special handling for interact-list
    if (selectedScript === 'interact-list') {
      const ilDefs = PARAM_DEFS['interact-list'] || [];
      const ilParams = {};
      for (const def of ilDefs) {
        let v = getVal(def.key);
        if (v == null && def.defaultVal != null) v = def.defaultVal;
        if (v != null) ilParams[def.key] = v;
      }
      const ilBody = {
        device_id: deviceId,
        account_id: accountId,
        app_id: pkgOverride?.app_id || selectedDevice?.app_id || 'com.instagram.android',
        custom_params: Object.keys(ilParams).length > 0 ? ilParams : undefined
      };
      launchScript('interact-list', '/api/devices/start-interact-list', ilBody);
      return;
    }
    const urls = {
      interact: '/api/devices/start-interact',
      scrape: '/api/devices/start-scrape',
      unfollow: '/api/unfollow',
      dm: '/api/devices/start-dm',
      'follower-analysis': '/api/devices/start-follower-analysis'
    };
    const body = { device_id: deviceId, account_id: accountId, open_terminal: openTerminal };
    // Pass app_id so backend picks the correct package row in instagram_device_configs
    const activeAppId = pkgOverride?.app_id || selectedDevice?.app_id || null;
    if (activeAppId) body.app_id = activeAppId;
    // Build full params: DB values + user overrides (dynamic slider values take priority)
    const defs = PARAM_DEFS[selectedScript] || [];
    const fullParams = {};
    for (const def of defs) {
      let v = getVal(def.key);
      // Always include all visible params — use displayed value (with defaults) so what user sees = what script uses
      if (v == null && def.defaultVal != null) v = def.defaultVal;
      if (v != null) fullParams[def.key] = v;
    }
    if (Object.keys(fullParams).length > 0) {
      body.custom_params = fullParams;
    }
    // Pass selected targets for interact
    if (selectedScript === 'interact') {
      const selArr = Array.from(selectedTargets);
      if (selArr.length < allTargets.length) {
        body.selected_targets = selArr;
      }
      if (manualTargets.length > 0) {
        body.manual_targets = manualTargets;
      }
    }
    // Scrape-specific fields: pass pool of selected scrape targets
    if (selectedScript === 'scrape') {
      const selScrapeArr = Array.from(selectedScrapeTargets);
      if (selScrapeArr.length > 0) {
        body.selected_scrape_targets = selScrapeArr;
        // Pick a random target from the pool for this run
        body.scrape_target = selScrapeArr[Math.floor(Math.random() * selScrapeArr.length)].replace(/^@/, '');
      } else if (scrapeTarget) {
        body.scrape_target = scrapeTarget.replace(/^@/, '');
      }
      body.scrape_type = scrapeType || 'followers';
    }
    // Unfollow-specific: pass type, count and sort order as top-level (backend reads from req.body)
    if (selectedScript === 'unfollow') {
      body.unfollow_type = fullParams.unfollow_type_script || getVal('unfollow_type_script') || 'database-global-search';
      if (fullParams.unfollow_count) body.unfollow_count = fullParams.unfollow_count;
      const sortOrder = fullParams.following_sort_order || getVal('following_sort_order');
      if (sortOrder) body.following_sort_order = sortOrder;
    }
    launchScript(selectedScript, urls[selectedScript], body);
  };

  // Target selector helpers
  const totalSelectedCount = selectedTargets.size + manualTargets.length;
  const allSelected = allTargets.length > 0 && selectedTargets.size === allTargets.length;

  const toggleTarget = (name) => {
    setSelectedTargets(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedTargets(new Set());
    } else {
      setSelectedTargets(new Set(allTargets.map(t => t.target_profile_name)));
    }
  };

  const addTargetToDB = async (purpose = 'interact') => {
    const raw = manualInput.trim();
    const isHash = raw.startsWith('#');
    const isPost = raw.includes('instagram.com/p/') || raw.includes('instagram.com/reel/');
    const val = isPost ? raw : raw.replace(/^[@#]/, '');
    if (!val || !accountId) return;
    const type = isPost ? 'post' : isHash ? 'hashtag' : 'user';
    try {
      const r = await fetch('/api/add-target', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: val, accountId, type, purpose })
      });
      const d = await r.json();
      if (d.success) {
        setManualInput('');
        if (purpose === 'scrape') fetchScrapeTargets();
        else { fetchTargets(); }
      } else {
        setLaunchError(d.error || d.message || 'Erreur');
        setTimeout(() => setLaunchError(null), 3000);
      }
    } catch (e) { setLaunchError('Erreur réseau'); setTimeout(() => setLaunchError(null), 3000); }
  };

  const deleteTarget = async (targetId, purpose = 'interact') => {
    try {
      await fetch(`/api/targets/${targetId}`, { method: 'DELETE' });
      if (purpose === 'scrape') fetchScrapeTargets();
      else fetchTargets();
    } catch { /* silent */ }
  };

  const toggleTargetSort = (key) => {
    setTargetSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'name' ? 'asc' : 'desc' });
  };

  const sortedTargets = [...allTargets].sort((a, b) => {
    const { key, dir } = targetSort;
    let cmp = 0;
    if (key === 'name') cmp = (a.target_profile_name || '').localeCompare(b.target_profile_name || '');
    else if (key === 'followers') cmp = (a.target_followers || 0) - (b.target_followers || 0);
    else if (key === 'interactions') cmp = (a.total_interactions || 0) - (b.total_interactions || 0);
    else if (key === 'users') cmp = (a.unique_users || 0) - (b.unique_users || 0);
    return dir === 'desc' ? -cmp : cmp;
  });

  const stopScript = async () => {
    if (!accountId) return;
    try {
      // 1. Mark DB as stopped + set forceStopped flag
      await fetch('/api/stop-script', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_id: accountId, device_id: deviceId })
      });
      // 2. Also call graceful stop by device_id (actually kills the process tree)
      if (deviceId) {
        await fetch('/api/stop-script-graceful', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ device_id: deviceId })
        }).catch(() => {});
      }
      setProcessInfo(null);
      setTimer('00:00:00');
      stoppedAtRef.current = Date.now();
    } catch (e) { /* silent */ }
  };

  const isRunning = !!processInfo?.isRunning;
  const runningScriptType = processInfo?.scriptType || processInfo?.process?.scriptType || null;
  const profileViews = parseInt(stats.profile_views) || 0;
  const likes = parseInt(stats.likes) || 0;
  const follows = parseInt(stats.follows) || 0;
  const storyViews = parseInt(stats.story_views) || 0;
  const storyLikes = parseInt(stats.story_likes) || 0;
  const unfollows = parseInt(stats.unfollows) || 0;
  const comments = parseInt(stats.comments) || 0;
  const scraped = parseInt(stats.scraped) || 0;
  const uniqueUsers = parseInt(stats.unique_users) || 0;

  // Render a single param control (slider, range, select, toggle, text)
  const activeScriptType = isRunning ? runningScriptType : selectedScript;
  const paramDefs = PARAM_DEFS[activeScriptType] || [];
  const disabled = isRunning;

  const renderParamControl = (def) => {
    const { key, label, type, min, max, step, unit } = def;

    if (type === 'slider') {
      const raw = getVal(key);
      const val = (raw != null) ? Number(raw) : (def.defaultVal != null ? Number(def.defaultVal) : (min || 0));
      const pct = sliderPct(val, min, max);
      return (
        <PRow key={key}>
          <PLabelRow><PLabel>{label}</PLabel><PValue>{val}{unit || ''}</PValue></PLabelRow>
          <Slider min={min} max={max} step={step || 1} value={val} $pct={pct}
            disabled={disabled} onChange={e => setParam(key, Number(e.target.value))} />
        </PRow>
      );
    }

    if (type === 'range') {
      const [lo, hi] = parseRange(getVal(key) ?? def.defaultVal);
      const loPct = sliderPct(lo, min, max);
      const hiPct = sliderPct(hi, min, max);
      return (
        <PRow key={key}>
          <PLabelRow><PLabel>{label}</PLabel><PValue>{lo} – {hi}</PValue></PLabelRow>
          <DualRangeWrap>
            <div className="dr-track" />
            <div className="dr-fill" style={{ left: `${loPct}%`, right: `${100 - hiPct}%` }} />
            <input type="range" min={min} max={max} step={step || 1} value={lo}
              disabled={disabled}
              onChange={e => { const v = Number(e.target.value); setParam(key, `${v}-${Math.max(hi, v)}`); }}
              style={{ zIndex: 3 }} />
            <input type="range" min={min} max={max} step={step || 1} value={hi}
              disabled={disabled}
              onChange={e => { const v = Number(e.target.value); setParam(key, `${Math.min(lo, v)}-${v}`); }}
              style={{ zIndex: 4 }} />
          </DualRangeWrap>
        </PRow>
      );
    }

    if (type === 'select') {
      if (def.showWhen && !def.showWhen(getVal)) return null;
      const val = getVal(key) || '';
      const selectOptions = def.options || UNFOLLOW_METHODS;
      return (
        <React.Fragment key={key}>
          <PRow>
            <PLabel>{label}</PLabel>
            <MethodGrid>
              {selectOptions.map(m => (
                <MethodOption key={m.value} $active={val === m.value} disabled={disabled}
                  onClick={() => { setParam(key, m.value); if (m.value === 'list') fetchUnfollowList(); }}>{m.label}</MethodOption>
              ))}
            </MethodGrid>
          </PRow>
          {key === 'unfollow_type_script' && val === 'list' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0 2px' }}>
              <button onClick={openUnfollowListModal} disabled={disabled}
                style={{ flex: 1, padding: '8px 0', borderRadius: 8, border: '1px dashed #f59e0b', background: 'rgba(245,158,11,.06)', color: '#f59e0b', fontWeight: 700, fontSize: 11, cursor: disabled ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: disabled ? .5 : 1 }}>
                <List size={13} /> Gérer la liste d'unfollow
                <span style={{ background: 'rgba(245,158,11,.15)', padding: '1px 7px', borderRadius: 10, fontSize: 10 }}>
                  {unfollowListEntries.filter(e => e.status === 'pending').length}
                </span>
              </button>
            </div>
          )}
        </React.Fragment>
      );
    }

    if (type === 'toggle') {
      const val = getVal(key);
      const isOn = val != null ? (val === 'Yes' || val === true || val === 'true') : (def.defaultVal === true);
      return (
        <ToggleRow key={key}>
          <ToggleLabel>{label}</ToggleLabel>
          <Toggle $on={isOn} $disabled={disabled} onClick={() => !disabled && setParam(key, isOn ? false : true)} />
        </ToggleRow>
      );
    }

    if (type === 'text') {
      const val = getVal(key) || '';
      return (
        <PRow key={key}>
          <PLabel style={{ marginBottom: 6, display: 'block' }}>{label}</PLabel>
          <DmTextarea value={val} disabled={disabled}
            onChange={e => setParam(key, e.target.value)}
            placeholder="Message 1&#10;Message 2&#10;..." />
        </PRow>
      );
    }
    return null;
  };

  if (!selectedDevice) {
    return (
      <Page>
        <EmptyState>
          <AlertCircle size={48} />
          <p>Sélectionnez un appareil pour voir l'automatisation</p>
        </EmptyState>
      </Page>
    );
  }

  return (
    <Page>
      {/* ── Top Banner ── */}
      <AutomationBanner
        platform="instagram"
        deviceName={selectedDevice.device_name || deviceId}
        isConnected={selectedDevice?.isConnected || selectedDevice?.is_online}
        isRunning={isRunning}
        timer={isRunning ? timer : null}
        runColor={isRunning ? getScriptColor(runningScriptType) : null}
        onNavigate={onNavigate}
        onStop={isRunning ? stopScript : null}
      />

      {launchError && (
        <div style={{ padding: '10px 16px', marginBottom: 12, borderRadius: 8, background: 'rgba(239,68,68,.12)', border: '1px solid rgba(239,68,68,.3)', color: '#ef4444', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertCircle size={14} /> {launchError}
          <button onClick={() => setLaunchError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>✕</button>
        </div>
      )}

      <ContentGrid>
        {/* ── Left Column ── */}
        <LeftCol>
          {/* Session Info (only when running) */}
          {isRunning && (
            <SessionCard $sc={getScriptColor(runningScriptType)}>
              <SessionDot $sc={getScriptColor(runningScriptType)} />
              <SessionInfo>
                <div className="session-type">{SCRIPT_TYPE_LABELS[runningScriptType] || 'Script'}</div>
                <div className="session-meta">Session #{sessionCount} · @{processInfo?.process?.username || selectedDevice.instagram_username || '—'}</div>
              </SessionInfo>
              <SessionTimer $sc={getScriptColor(runningScriptType)}>{timer}</SessionTimer>
            </SessionCard>
          )}

          {/* Active Account + Script Selector */}
          <AccountCard>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <AccountLabel style={{ margin: 0 }}>Compte actif</AccountLabel>
              <select
                value={`${accountId || ''}|${activeAppId}`}
                onChange={e => switchToAccount(e.target.value)}
                style={{
                  flex: 1, padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border)',
                  background: 'var(--surface)', color: 'var(--text-primary)', fontSize: 12, fontWeight: 600,
                  cursor: 'pointer', outline: 'none', maxWidth: 280
                }}
              >
                {deviceAccounts.length > 0 ? deviceAccounts.map(da => (
                  <option key={`${da.account_id}|${da.app_id || 'com.instagram.android'}`} value={`${da.account_id}|${da.app_id || 'com.instagram.android'}`}>
                    @{da.username || `ID ${da.account_id}`} — {da.package_label || da.app_id || 'Instagram'}
                  </option>
                )) : (
                  <option value={`${accountId || ''}|${activeAppId}`}>@{accountUsername || '—'}</option>
                )}
              </select>
            </div>
            <AccountCardGrid>
              <AccountLeft>
                <AccountAvatar>
                  {accountUsername ? (
                    <img src={getLocalProfilePicUrl(accountUsername)} alt=""
                      onError={e => {
                        const step = parseInt(e.target.dataset.fb || '0');
                        if (step === 0) { e.target.dataset.fb = '1'; e.target.src = getProfilePicUrl(accountUsername); }
                        else if (step === 1) { e.target.dataset.fb = '2'; e.target.src = `https://unavatar.io/instagram/${accountUsername}`; }
                        else { e.target.style.display = 'none'; }
                      }} />
                  ) : null}
                  {(accountUsername || '?')[0].toUpperCase()}
                </AccountAvatar>
                <AccountName>
                  @{accountUsername || '—'}
                  <span>{selectedDevice.device_name || deviceId}</span>
                </AccountName>
              </AccountLeft>
              <AccountRight>
                <DayStat $bg="rgba(239,68,68,.08)" $color="#ef4444">
                  <Heart size={12} style={{ color: '#ef4444', flexShrink: 0 }} />
                  <div><div className="ds-val">{likes}</div><div className="ds-label">Likes</div></div>
                </DayStat>
                <DayStat $bg="rgba(34,197,94,.08)" $color="#22c55e">
                  <UserPlus size={12} style={{ color: '#22c55e', flexShrink: 0 }} />
                  <div><div className="ds-val">{follows}</div><div className="ds-label">Follows</div></div>
                </DayStat>
                <DayStat $bg="rgba(165,145,239,.08)" $color="#a591ef">
                  <UserCheck size={12} style={{ color: '#a591ef', flexShrink: 0 }} />
                  <div><div className="ds-val">{profileViews}</div><div className="ds-label">Profils</div></div>
                </DayStat>
                <DayStat $bg="rgba(6,182,212,.08)" $color="#06b6d4">
                  <Scan size={12} style={{ color: '#06b6d4', flexShrink: 0 }} />
                  <div><div className="ds-val">{scraped}</div><div className="ds-label">Scrapés</div></div>
                </DayStat>
                <DayStat $bg="rgba(249,115,22,.08)" $color="#f97316">
                  <UserMinus size={12} style={{ color: '#f97316', flexShrink: 0 }} />
                  <div><div className="ds-val">{unfollows}</div><div className="ds-label">Unfollows</div></div>
                </DayStat>
                <DayStat $bg="rgba(245,158,11,.08)" $color="#f59e0b">
                  <BookOpen size={12} style={{ color: '#f59e0b', flexShrink: 0 }} />
                  <div><div className="ds-val">{storyViews}</div><div className="ds-label">Stories</div></div>
                </DayStat>
                <DayStat $bg="rgba(236,72,153,.08)" $color="#ec4899">
                  <Star size={12} style={{ color: '#ec4899', flexShrink: 0 }} />
                  <div><div className="ds-val">{storyLikes}</div><div className="ds-label">S.Likes</div></div>
                </DayStat>
                <DayStat $bg="rgba(59,130,246,.08)" $color="#3b82f6">
                  <MessageCircle size={12} style={{ color: '#3b82f6', flexShrink: 0 }} />
                  <div><div className="ds-val">{comments}</div><div className="ds-label">Comments</div></div>
                </DayStat>
                <DayStat $bg="rgba(139,92,246,.08)" $color="#8b5cf6">
                  <Users size={12} style={{ color: '#8b5cf6', flexShrink: 0 }} />
                  <div><div className="ds-val">{uniqueUsers}</div><div className="ds-label">Uniques</div></div>
                </DayStat>
              </AccountRight>
            </AccountCardGrid>
            {!isRunning && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
                <ScriptSelector style={{ flex: 1 }}>
                  {['interact', 'scrape', 'unfollow', 'dm', 'detect', 'follower-analysis', 'like-comment', 'interact-post-likers', 'interact-list'].map(type => (
                    <ScriptTypeBtn
                      key={type}
                      $variant={type}
                      $selected={selectedScript === type}
                      disabled={launching}
                      onClick={() => setSelectedScript(prev => prev === type ? null : type)}
                    >
                      {type === 'interact' && <Zap size={11} />}
                      {type === 'scrape' && <SearchIcon size={11} />}
                      {type === 'unfollow' && <UserMinus size={11} />}
                      {type === 'dm' && <Send size={11} />}
                      {type === 'detect' && <Eye size={11} />}
                      {type === 'follower-analysis' && <Users size={11} />}
                      {type === 'like-comment' && <ThumbsUp size={11} />}
                      {type === 'interact-post-likers' && <Heart size={11} />}
                      {type === 'interact-list' && <List size={11} />}
                      {SCRIPT_TYPE_LABELS[type]}
                    </ScriptTypeBtn>
                  ))}
                </ScriptSelector>
                <LaunchBtn
                  $variant={selectedScript}
                  disabled={!selectedScript || launching || (!accountId && selectedScript !== 'detect')}
                  onClick={handleLaunch}
                >
                  <Play size={12} /> {launching ? '...' : 'Lancer'}
                </LaunchBtn>
              </div>
            )}
            {!isRunning && selectedScript && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-muted)', cursor: 'pointer', marginTop: 6, userSelect: 'none' }}>
                <input type="checkbox" checked={openTerminal} onChange={e => setOpenTerminal(e.target.checked)} style={{ accentColor: 'var(--primary)' }} />
                Ouvrir un terminal pour les logs du script
              </label>
            )}
          </AccountCard>

          {/* Script Parameters with sliders — collapsible */}
          {paramDefs.length > 0 && (activeScriptType || isRunning) && (
            <ParamSection>
              <ParamSectionHeader $collapsed={!paramsExpanded} onClick={() => setParamsExpanded(p => !p)}>
                <Settings size={13} />
                Paramètres {SCRIPT_TYPE_LABELS[activeScriptType] || ''}
                <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
                  {!isRunning && deviceId && (
                    <button
                      onClick={(e) => { e.stopPropagation(); saveParams(); }}
                      disabled={savingParams}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '3px 10px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                        border: saveSuccess ? '1px solid #22c55e' : '1px solid var(--border)',
                        background: saveSuccess ? 'rgba(34,197,94,.12)' : 'var(--surface)',
                        color: saveSuccess ? '#22c55e' : 'var(--text-secondary)',
                        cursor: savingParams ? 'wait' : 'pointer',
                        transition: 'all .2s',
                      }}
                      title="Enregistrer les paramètres en base de données"
                    >
                      {saveSuccess ? <><Check size={10} /> Sauvegardé</> : savingParams ? '...' : <><Save size={10} /> Enregistrer</>}
                    </button>
                  )}
                  {paramsExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </span>
              </ParamSectionHeader>
              {paramsExpanded && (() => {
                const singleSliders = paramDefs.filter(d => d.type === 'slider');
                const rangeSliders = paramDefs.filter(d => d.type === 'range');
                const others = paramDefs.filter(d => d.type !== 'slider' && d.type !== 'range');
                // Likes + Stories side by side
                const likeStoryPair = rangeSliders.filter(d => d.key === 'likes_count' || d.key === 'stories_count');
                // Session + Interactions side by side
                const sessionInterPair = rangeSliders.filter(d => d.key === 'session_length_in_mins_limit' || d.key === 'successful_interactions_limit_per_source');
                const pairedKeys = new Set(['likes_count', 'stories_count', 'session_length_in_mins_limit', 'successful_interactions_limit_per_source']);
                const otherRanges = rangeSliders.filter(d => !pairedKeys.has(d.key));
                return (
                  <>
                    {likeStoryPair.length === 2 && (
                      <RangeSliderRow>{likeStoryPair.map(renderParamControl)}</RangeSliderRow>
                    )}
                    {likeStoryPair.length !== 2 && likeStoryPair.map(renderParamControl)}
                    {sessionInterPair.length === 2 && (
                      <RangeSliderRow>{sessionInterPair.map(renderParamControl)}</RangeSliderRow>
                    )}
                    {sessionInterPair.length !== 2 && sessionInterPair.map(renderParamControl)}
                    {otherRanges.map(renderParamControl)}
                    {singleSliders.length > 0 && <CompactGrid>{singleSliders.map(renderParamControl)}</CompactGrid>}
                    {others.map(renderParamControl)}
                  </>
                );
              })()}
            </ParamSection>
          )}

          {/* Like Comment Section (shown for like-comment) */}
          {selectedScript === 'like-comment' && !isRunning && (
            <ParamSection>
              <ParamSectionHeader>
                <ThumbsUp size={13} />
                Like Comment — Paramètres
              </ParamSectionHeader>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '2px 0' }}>
                {/* Saved targets list */}
                {likeCommentSavedTargets.length > 0 && (
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6 }}>
                      <Star size={11} style={{ marginRight: 4, verticalAlign: -1 }} />
                      Cibles enregistrées ({likeCommentSavedTargets.length})
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 180, overflowY: 'auto' }}>
                      {likeCommentSavedTargets.map((t, idx) => {
                        const isActive = likeCommentPostUrl === t.url && likeCommentText === t.comment;
                        const shortUrl = t.url.replace(/^https?:\/\/(www\.)?instagram\.com\/p\//, '/p/').replace(/\?.*$/, '');
                        return (
                          <div key={idx} style={{
                            display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px',
                            borderRadius: 6, fontSize: 11, cursor: 'pointer',
                            border: isActive ? '1.5px solid #f43f5e' : '1px solid var(--border)',
                            background: isActive ? 'rgba(244,63,94,0.08)' : 'var(--surface)',
                            transition: 'all .15s'
                          }} onClick={() => { setLikeCommentPostUrl(t.url); setLikeCommentText(t.comment); }}>
                            <Heart size={12} style={{ color: isActive ? '#f43f5e' : 'var(--text-muted)', flexShrink: 0 }} fill={isActive ? '#f43f5e' : 'none'} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{shortUrl}</div>
                              <div style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 10 }}>💬 {t.comment}</div>
                            </div>
                            <button onClick={e => { e.stopPropagation(); saveLikeCommentTargets(likeCommentSavedTargets.filter((_, i) => i !== idx)); }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--text-muted)', display: 'flex' }}
                              title="Supprimer">
                              <X size={12} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 4 }}>
                    <Link size={11} style={{ marginRight: 4, verticalAlign: -1 }} />
                    URL du post Instagram
                  </label>
                  <input
                    type="text"
                    value={likeCommentPostUrl}
                    onChange={e => setLikeCommentPostUrl(e.target.value)}
                    placeholder="https://www.instagram.com/p/ABC123/ ou shortcode"
                    style={{
                      width: '100%', padding: '8px 12px', borderRadius: 8,
                      border: '1px solid var(--border)', background: 'var(--surface)',
                      color: 'var(--text-primary)', fontSize: 12, outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 4 }}>
                    <MessageCircle size={11} style={{ marginRight: 4, verticalAlign: -1 }} />
                    Texte du commentaire à liker (nom d'utilisateur ou texte)
                  </label>
                  <input
                    type="text"
                    value={likeCommentText}
                    onChange={e => setLikeCommentText(e.target.value)}
                    placeholder="Ex: Rolli, @username, ou texte du commentaire"
                    style={{
                      width: '100%', padding: '8px 12px', borderRadius: 8,
                      border: '1px solid var(--border)', background: 'var(--surface)',
                      color: 'var(--text-primary)', fontSize: 12, outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {likeCommentPostUrl && likeCommentText && (
                    <button onClick={() => {
                      const exists = likeCommentSavedTargets.some(t => t.url === likeCommentPostUrl && t.comment === likeCommentText);
                      if (!exists) {
                        saveLikeCommentTargets([...likeCommentSavedTargets, { url: likeCommentPostUrl, comment: likeCommentText, addedAt: new Date().toISOString() }]);
                      }
                    }} style={{
                      padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)',
                      background: 'var(--surface)', color: 'var(--text-secondary)', fontSize: 11,
                      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600
                    }}>
                      <Save size={11} /> Enregistrer
                    </button>
                  )}
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                  🧠 Utilise Gemini Vision pour identifier le commentaire visuellement puis clique sur le bouton ❤️ correspondant.
                </div>
              </div>
            </ParamSection>
          )}

          {/* Post Likers Section (shown for interact-post-likers) */}
          {selectedScript === 'interact-post-likers' && !isRunning && (
            <ParamSection>
              <ParamSectionHeader>
                <Heart size={13} />
                Post Likers — URL du post
              </ParamSectionHeader>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '2px 0' }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 4 }}>
                    <Link size={11} style={{ marginRight: 4, verticalAlign: -1 }} />
                    URL du post Instagram
                  </label>
                  <input
                    type="text"
                    value={postLikersUrl}
                    onChange={e => setPostLikersUrl(e.target.value)}
                    placeholder="https://www.instagram.com/p/ABC123/ ou https://www.instagram.com/reel/ABC123/"
                    style={{
                      width: '100%', padding: '8px 12px', borderRadius: 8,
                      border: '1px solid var(--border)', background: 'var(--surface)',
                      color: 'var(--text-primary)', fontSize: 12, outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                  Le script va naviguer vers ce post, ouvrir la liste des likers, puis interagir avec chacun d'entre eux (like, follow, stories selon les paramètres ci-dessus).
                </div>
              </div>
            </ParamSection>
          )}

          {/* Scrape Targets Section (shown for scrape) */}
          {(selectedScript === 'scrape' || (isRunning && runningScriptType === 'scrape')) && !isRunning && (
            <ParamSection>
              <ParamSectionHeader>
                <Target size={13} />
                Cibles de scrape
                <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 500, color: 'var(--text-muted)', textTransform: 'none', letterSpacing: 0 }}>
                  {selectedScrapeTargets.size} sélectionnée{selectedScrapeTargets.size !== 1 ? 's' : ''} / {scrapeTargets.length} en base
                </span>
              </ParamSectionHeader>

              {/* Select all / deselect all */}
              {scrapeTargets.length > 0 && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                  <button onClick={() => setSelectedScrapeTargets(new Set(scrapeTargets.map(t => t.target_profile_name)))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', fontSize: 11, fontWeight: 700 }}>
                    Tout sélectionner
                  </button>
                  <button onClick={() => setSelectedScrapeTargets(new Set())}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 11, fontWeight: 600 }}>
                    Tout désélectionner
                  </button>
                </div>
              )}

              {/* Saved scrape targets with checkboxes */}
              {scrapeTargets.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 0, marginBottom: 8 }}>
                  {scrapeTargets.map(t => {
                    const sel = selectedScrapeTargets.has(t.target_profile_name);
                    return (
                      <div key={t.id} style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                        borderBottom: '1px solid var(--border)', fontSize: 12,
                        background: sel ? 'rgba(165,145,239,.06)' : 'transparent', cursor: 'pointer'
                      }} onClick={() => {
                        setSelectedScrapeTargets(prev => {
                          const next = new Set(prev);
                          if (next.has(t.target_profile_name)) next.delete(t.target_profile_name); else next.add(t.target_profile_name);
                          return next;
                        });
                      }}>
                        <div style={{
                          width: 18, height: 18, borderRadius: 4, border: `2px solid ${sel ? '#22c55e' : 'var(--card-border)'}`,
                          background: sel ? '#22c55e' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#fff', fontSize: 11, fontWeight: 700, flexShrink: 0
                        }}>{sel ? '✓' : ''}</div>
                        <TargetAvatar>
                          <img
                            src={getProfilePicUrl(t.target_profile_name)}
                            alt=""
                            onError={e => { if (!e.target.dataset.fb) { e.target.dataset.fb='1'; e.target.src=`https://unavatar.io/instagram/${t.target_profile_name}`; } else e.target.style.display='none'; }}
                          />
                          {(t.target_profile_name || '?')[0].toUpperCase()}
                        </TargetAvatar>
                        <span style={{ flex: 1, fontWeight: 700, color: sel ? 'var(--primary)' : 'var(--text-primary)' }}>
                          @{t.target_profile_name}
                        </span>
                        {sel && (
                          <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase' }}>sélectionnée</span>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); deleteTarget(t.id, 'scrape'); }} style={{
                          background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2
                        }}><X size={12} /></button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Add new scrape target */}
              <ManualInputRow>
                <ManualInput
                  placeholder="@username à scraper"
                  value={manualInput}
                  onChange={e => setManualInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') addTargetToDB('scrape'); }}
                />
                <ManualAddBtn onClick={() => addTargetToDB('scrape')} disabled={!manualInput.trim()}>
                  <Plus size={14} />
                </ManualAddBtn>
              </ManualInputRow>

              <PRow style={{ marginTop: 8 }}>
                <PLabelRow><PLabel>Type de scrape</PLabel></PLabelRow>
                <MethodGrid>
                  {[{ value: 'followers', label: 'Followers' }, { value: 'following', label: 'Following' }].map(m => (
                    <MethodOption key={m.value} $active={scrapeType === m.value}
                      onClick={() => setScrapeType(m.value)}>{m.label}</MethodOption>
                  ))}
                </MethodGrid>
              </PRow>
            </ParamSection>
          )}

          {/* Target Selector (shown for interact) */}
          {(selectedScript === 'interact' || (isRunning && runningScriptType === 'interact')) && (
            <TargetCard>
              <TargetHeader onClick={() => setTargetsExpanded(p => !p)}>
                <Target size={13} />
                Cibles d'interaction
                <TargetCount $count={totalSelectedCount}>
                  {totalSelectedCount} sélectionnée{totalSelectedCount !== 1 ? 's' : ''}
                </TargetCount>
                {targetsExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </TargetHeader>
              {targetsExpanded && (
                <TargetBody>
                  {allTargets.length > 0 ? (
                    <>
                      <TargetActions>
                        <SelectAllBtn $active={allSelected} onClick={toggleAll}>
                          {allSelected ? <CheckSquare size={12} /> : <Square size={12} />}
                          {allSelected ? 'Désélectionner tout' : 'Tout sélectionner'}
                        </SelectAllBtn>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          {allTargets.length} cible{allTargets.length !== 1 ? 's' : ''} en base
                        </span>
                      </TargetActions>
                      <TargetTableHead>
                        <span></span>
                        <SortHeader $active={targetSort.key === 'name'} onClick={() => toggleTargetSort('name')}>
                          Cible {targetSort.key === 'name' && (targetSort.dir === 'asc' ? '↑' : '↓')}
                        </SortHeader>
                        <SortHeader $active={targetSort.key === 'followers'} onClick={() => toggleTargetSort('followers')} style={{ justifyContent: 'flex-end' }}>
                          Followers {targetSort.key === 'followers' && (targetSort.dir === 'asc' ? '↑' : '↓')}
                        </SortHeader>
                        <SortHeader $active={targetSort.key === 'interactions'} onClick={() => toggleTargetSort('interactions')} style={{ justifyContent: 'flex-end' }}>
                          Interact. {targetSort.key === 'interactions' && (targetSort.dir === 'asc' ? '↑' : '↓')}
                        </SortHeader>
                        <SortHeader $active={targetSort.key === 'users'} onClick={() => toggleTargetSort('users')} style={{ justifyContent: 'flex-end' }}>
                          Users {targetSort.key === 'users' && (targetSort.dir === 'asc' ? '↑' : '↓')}
                        </SortHeader>
                        <span></span>
                      </TargetTableHead>
                      <TargetList>
                        {sortedTargets.map(t => {
                          const tType = t.target_type || 'user';
                          const isHash = tType === 'hashtag';
                          const isPost = tType === 'post' || tType === 'post_url';
                          const sel = selectedTargets.has(t.target_profile_name);
                          const displayName = isPost
                            ? (t.target_profile_name.match(/\/p\/([A-Za-z0-9_-]+)/)?.[1] || t.target_profile_name).substring(0, 20)
                            : t.target_profile_name;
                          return (
                            <TargetTableRow
                              key={t.id || t.target_profile_name}
                              $selected={sel}
                              onClick={() => toggleTarget(t.target_profile_name)}
                            >
                              <TargetAvatar>
                                {!isHash && !isPost ? (
                                  <img
                                    src={getProfilePicUrl(t.target_profile_name)}
                                    alt=""
                                    onError={e => { if (!e.target.dataset.fb) { e.target.dataset.fb='1'; e.target.src=`https://unavatar.io/instagram/${t.target_profile_name}`; } else e.target.style.display='none'; }}
                                  />
                                ) : null}
                                {isHash ? <Hash size={12} /> : isPost ? <FileText size={12} /> : t.target_profile_name[0]?.toUpperCase()}
                              </TargetAvatar>
                              <TargetNameCell>
                                {sel ? <Check size={10} style={{ marginRight: 3, verticalAlign: 'middle' }} /> : null}
                                {isPost ? '📌 ' : isHash ? '#' : '@'}{displayName}
                                {t.target_weight > 1 && <span style={{ opacity: .5, marginLeft: 3 }}>×{t.target_weight}</span>}
                              </TargetNameCell>
                              <TargetNumCell style={{ fontSize: 10, color: t.target_followers ? 'var(--text-secondary)' : 'var(--text-muted)', opacity: t.target_followers ? 1 : .4 }}>
                                {t.target_followers ? (t.target_followers >= 1000 ? `${(t.target_followers / 1000).toFixed(1)}k` : t.target_followers) : '-'}
                              </TargetNumCell>
                              <TargetNumCell>{t.total_interactions || 0}</TargetNumCell>
                              <TargetNumCell>{t.unique_users || 0}</TargetNumCell>
                              <div style={{ display: 'flex', justifyContent: 'center' }}>
                                {(() => {
                                  const total = t.target_followers || 0;
                                  const done = t.unique_users || 0;
                                  const pct = total > 0 ? Math.min(done / total, 1) : 0;
                                  const r = 10, stroke = 2.5, circ = 2 * Math.PI * r;
                                  const color = pct >= 0.5 ? '#22c55e' : pct >= 0.1 ? '#f59e0b' : '#a591ef';
                                  return (
                                    <svg width="24" height="24" viewBox="0 0 24 24" title={total > 0 ? `${(pct * 100).toFixed(1)}% (${done}/${total})` : 'N/A'}>
                                      <circle cx="12" cy="12" r={r} fill="none" stroke="var(--border)" strokeWidth={stroke} />
                                      {total > 0 && <circle cx="12" cy="12" r={r} fill="none" stroke={color} strokeWidth={stroke}
                                        strokeDasharray={`${circ * pct} ${circ * (1 - pct)}`}
                                        strokeDashoffset={circ * 0.25} strokeLinecap="round" />}
                                      <text x="12" y="12" textAnchor="middle" dominantBaseline="central" fill={total > 0 ? color : 'var(--text-muted)'}
                                        fontSize="6" fontWeight="800" fontFamily="'JetBrains Mono', monospace">
                                        {total > 0 ? `${Math.round(pct * 100)}` : '-'}
                                      </text>
                                    </svg>
                                  );
                                })()}
                              </div>
                            </TargetTableRow>
                          );
                        })}
                      </TargetList>
                    </>
                  ) : (
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '4px 0' }}>
                      Aucune cible configurée — ajoutez-en manuellement ci-dessous
                    </div>
                  )}

                  {/* Add interact target to DB */}
                  <ManualInputRow>
                    <ManualInput
                      placeholder="@username, #hashtag ou URL de post"
                      value={manualInput}
                      onChange={e => setManualInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') addTargetToDB('interact'); }}
                    />
                    <ManualAddBtn onClick={() => addTargetToDB('interact')} disabled={!manualInput.trim()}>
                      <Plus size={14} />
                    </ManualAddBtn>
                  </ManualInputRow>
                </TargetBody>
              )}
            </TargetCard>
          )}

        </LeftCol>

        {/* ── Right Column: Unified Live Activity + Sessions ── */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <ActivityPanel>
            <ActivityHeader>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Zap size={16} style={{ color: 'var(--primary)' }} />
                Dernières actions
                {(() => {
                  const first = actions[0];
                  return first ? (
                    <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted)' }}>
                      — {fmtAgo(first.timestamp)}
                    </span>
                  ) : null;
                })()}
              </div>
              <ActivityCount>
                {`${actions.length} événements`}
              </ActivityCount>
            </ActivityHeader>

            {/* Live session indicator + stats */}
            {isRunning && (
              <div style={{ borderBottom: '1px solid var(--border)', background: `${getScriptColor(runningScriptType).bg}0.06)` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 18px' }}>
                  <SessionDot $sc={getScriptColor(runningScriptType)} style={{ width: 8, height: 8 }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: getScriptColor(runningScriptType).main }}>
                    Session en cours — {SCRIPT_TYPE_LABELS[runningScriptType] || 'Script'}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto', fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>
                    {timer}
                  </span>
                </div>
                {/* Live session action counters — use todaySessions aggregated counts for accuracy */}
                {(() => {
                  const currentSessionId = processInfo?.session_id || processInfo?.process?.session_id;
                  // Find the matching session from todaySessions (server-side aggregation, no limit)
                  const sessData = currentSessionId
                    ? todaySessions.find(s => String(s.session_id) === String(currentSessionId))
                    : null;
                  let counts;
                  if (sessData) {
                    // Use accurate server-side counts
                    counts = {
                      GetProfileAction: parseInt(sessData.profiles) || 0,
                      LikeAction: parseInt(sessData.likes) || 0,
                      FollowAction: parseInt(sessData.follows) || 0,
                      UnfollowAction: parseInt(sessData.unfollows) || 0,
                      StoryWatchAction: parseInt(sessData.story_views) || 0,
                      StoryLikeAction: parseInt(sessData.story_likes) || 0,
                      ScrapeAction: parseInt(sessData.scrapes) || 0,
                      CommentAction: parseInt(sessData.comments) || 0,
                    };
                  } else {
                    // Fallback: count from local actions array (may be limited)
                    counts = {};
                    const sessionActions = currentSessionId
                      ? actions.filter(a => String(a.session_id) === String(currentSessionId))
                      : actions;
                    sessionActions.forEach(a => {
                      const t = a.type || 'unknown';
                      counts[t] = (counts[t] || 0) + 1;
                    });
                  }
                  const statDefs = [
                    { key: 'GetProfileAction', label: 'Profils', color: '#3b82f6', Icon: Eye },
                    { key: 'LikeAction', label: 'Likes', color: '#ef4444', Icon: Heart },
                    { key: 'FollowAction', label: 'Follows', color: '#22c55e', Icon: UserPlus },
                    { key: 'UnfollowAction', label: 'Unfollows', color: '#f59e0b', Icon: UserMinus },
                    { key: 'StoryWatchAction', label: 'Stories', color: '#ec4899', Icon: BookOpen },
                    { key: 'StoryLikeAction', label: 'Story ♥', color: '#8b5cf6', Icon: Star },
                    { key: 'ScrapeAction', label: 'Scrapes', color: '#a855f7', Icon: Scan },
                    { key: 'CommentAction', label: 'Comments', color: '#06b6d4', Icon: MessageCircle },
                  ];
                  const statItems = statDefs.filter(s => counts[s.key] > 0);
                  if (statItems.length === 0) return null;
                  return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '6px 18px 10px' }}>
                      {statItems.map(s => (
                        <div key={s.key} style={{
                          display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6,
                          background: `${s.color}12`, fontSize: 11, fontWeight: 700
                        }}>
                          <s.Icon size={10} style={{ color: s.color }} />
                          <span style={{ color: s.color, fontFamily: "'JetBrains Mono', monospace" }}>{counts[s.key]}</span>
                          <span style={{ color: 'var(--text-muted)', fontSize: 9, fontWeight: 600 }}>{s.label}</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}

            <ActivityList>
              {(() => {
                const allActions = [...actions];

                if (allActions.length === 0 && !loading) {
                  return (
                    <EmptyState style={{ padding: 30 }}>
                      <Activity size={32} />
                      <p>Aucune activité récente</p>
                    </EmptyState>
                  );
                }

                let lastDay = null;
                let dayIdx = 0;
                return allActions.map(a => {
                  const meta = ACTION_MAP[a.type] || { label: a.type, icon: Activity, color: 'var(--text-muted)', bg: 'var(--surface)' };
                  const Icon = meta.icon;
                  const targetUser = a.target_user || '...';
                  const actionDate = a.timestamp ? new Date(a.timestamp) : null;
                  const dayKey = actionDate ? `${actionDate.getFullYear()}-${actionDate.getMonth()}-${actionDate.getDate()}` : null;
                  let showSep = false;
                  if (dayKey && dayKey !== lastDay) {
                    showSep = true;
                    if (lastDay !== null) dayIdx++;
                    lastDay = dayKey;
                  }
                  const alt = dayIdx % 2 === 1;
                  return (
                    <React.Fragment key={a.id}>
                      {showSep && <DaySeparator>{fmtDateLabel(a.timestamp)}</DaySeparator>}
                      <ActivityItem $alt={alt} $isNew={!!a._isNew}>
                        <ActionIconWrap $bg={meta.bg} $color={meta.color}>
                          {meta.showProfilePic && targetUser !== '...' && (
                            <img
                              src={getLocalProfilePicUrl(targetUser)}
                              alt=""
                              onError={e => {
                                const step = parseInt(e.target.dataset.fb || '0');
                                if (step === 0) { e.target.dataset.fb = '1'; e.target.src = getProfilePicUrl(targetUser); }
                                else if (step === 1) { e.target.dataset.fb = '2'; e.target.src = `https://unavatar.io/instagram/${targetUser}`; }
                                else { e.target.style.display = 'none'; }
                              }}
                            />
                          )}
                          <Icon size={14} />
                        </ActionIconWrap>
                        <ActionText>
                          {meta.label} <strong>@{targetUser}</strong>
                        </ActionText>
                        <ActionTime>
                          {a.source && <span style={{ color: 'var(--primary)', fontSize: '10px', fontWeight: 500, marginRight: 6, opacity: 0.7 }}>@{a.source}</span>}
                          {fmtTime(a.timestamp)}
                        </ActionTime>
                      </ActivityItem>
                    </React.Fragment>
                  );
                });
              })()}
            </ActivityList>
          </ActivityPanel>

          {/* ── Sessions Section (below actions) ── */}
          {todaySessions.length > 0 && (
            <SessionsPanel>
              <SessionsHeader onClick={() => setSessionsExpanded(p => !p)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Clock size={14} style={{ color: 'var(--primary)' }} />
                  Sessions du jour
                  <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted)' }}>
                    ({todaySessions.length})
                  </span>
                </div>
                {sessionsExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </SessionsHeader>
              {sessionsExpanded && todaySessions.map((sess, idx) => {
                const profiles = parseInt(sess.profiles) || 0;
                const uniqueUsers = parseInt(sess.unique_users) || 0;
                const likes = parseInt(sess.likes) || 0;
                const follows = parseInt(sess.follows) || 0;
                const maxProfiles = Math.max(...todaySessions.map(s => parseInt(s.profiles) || 1), 1);
                const pct = Math.min((profiles / maxProfiles) * 100, 100);
                const isLast = idx === todaySessions.length - 1;
                const isActive = isLast && isRunning;

                const startTime = sess.started_at ? new Date(sess.started_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—';
                const endTime = sess.ended_at ? new Date(sess.ended_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—';

                // Duration
                let durationStr = '—';
                if (sess.started_at) {
                  const endMs = isActive ? Date.now() : (sess.ended_at ? new Date(sess.ended_at).getTime() : Date.now());
                  const diffSec = Math.floor((endMs - new Date(sess.started_at).getTime()) / 1000);
                  if (diffSec >= 3600) durationStr = `${Math.floor(diffSec / 3600)}h${Math.floor((diffSec % 3600) / 60).toString().padStart(2, '0')}`;
                  else durationStr = `${Math.floor(diffSec / 60)}min`;
                }

                return (
                  <SessionRow key={sess.session_id || idx}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: isActive ? 'var(--success)' : 'var(--text-muted)', flexShrink: 0, opacity: isActive ? 1 : .4 }} />
                    <SessionBarWrap>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: isActive ? 'var(--success)' : 'var(--text-primary)' }}>
                          Session {idx + 1}
                        </span>
                        <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                          {startTime} → {isActive ? 'en cours' : endTime}
                        </span>
                      </div>
                      <ProgressBarOuter style={{ height: 5 }}>
                        <ProgressBarFill $pct={pct} style={isActive ? { background: 'linear-gradient(90deg, var(--success), #22c55e)' } : {}} />
                      </ProgressBarOuter>
                    </SessionBarWrap>
                    <SessionMeta>
                      <SessionBadge $bg="rgba(165,145,239,.1)" $color="var(--primary)">{uniqueUsers} users</SessionBadge>
                      <SessionBadge $bg="rgba(34,197,94,.1)" $color="var(--success)">{durationStr}</SessionBadge>
                      {likes > 0 && <SessionBadge $bg="rgba(239,68,68,.08)" $color="#ef4444">♥ {likes}</SessionBadge>}
                      {follows > 0 && <SessionBadge $bg="rgba(34,197,94,.08)" $color="#22c55e">+ {follows}</SessionBadge>}
                    </SessionMeta>
                  </SessionRow>
                );
              })}
            </SessionsPanel>
          )}
        </div>
      </ContentGrid>

      {/* ── Unfollow List Modal ── */}
      {showUnfollowListModal && (() => {
        const pendingEntries = unfollowListEntries.filter(e => e.status === 'pending');
        const doneEntries = unfollowListEntries.filter(e => e.status !== 'pending');
        const listedUsernames = new Set(unfollowListEntries.map(e => e.target_username?.toLowerCase()));
        const filtered = interactedProfiles.filter(p =>
          p.username.toLowerCase().includes(pickerSearch.toLowerCase()) &&
          !listedUsernames.has(p.username.toLowerCase())
        );
        const allVisibleSelected = filtered.length > 0 && filtered.every(p => selectedProfiles.has(p.username));
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => setShowUnfollowListModal(false)}>
            <div onClick={e => e.stopPropagation()} style={{
              width: '95%', maxWidth: 900, maxHeight: '90vh', background: 'var(--card-bg)',
              borderRadius: 16, border: '1px solid var(--card-border)', display: 'flex', flexDirection: 'column',
              boxShadow: '0 25px 50px rgba(0,0,0,.25)', overflow: 'hidden'
            }}>
              {/* Header */}
              <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <List size={18} style={{ color: '#f59e0b' }} />
                  <span style={{ fontWeight: 700, fontSize: 15 }}>Liste d'unfollow — @{accountUsername}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', background: 'var(--surface)', padding: '2px 8px', borderRadius: 6 }}>
                    {pendingEntries.length} en attente
                  </span>
                  {doneEntries.length > 0 && (
                    <span style={{ fontSize: 11, color: '#22c55e', background: 'rgba(34,197,94,.1)', padding: '2px 8px', borderRadius: 6 }}>
                      {doneEntries.length} traités
                    </span>
                  )}
                </div>
                <button onClick={() => setShowUnfollowListModal(false)}
                  style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                  <X size={20} />
                </button>
              </div>

              {/* Body: 2 columns */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', flex: 1, overflow: 'hidden', minHeight: 0 }}>
                {/* Left: current list + bulk add */}
                <div style={{ borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <FileText size={13} style={{ color: '#f59e0b' }} /> Liste actuelle ({pendingEntries.length} en attente)
                  </div>
                  <div style={{ flex: 1, overflowY: 'auto', padding: '8px 12px', maxHeight: 300 }}>
                    {unfollowListLoading ? (
                      <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: 12 }}>Chargement...</div>
                    ) : pendingEntries.length === 0 && doneEntries.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: 12 }}>Liste vide — ajoutez des comptes à unfollow</div>
                    ) : (<>
                      {pendingEntries.map((entry, i) => (
                        <div key={entry.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 8px', borderRadius: 6, fontSize: 12, background: i % 2 === 0 ? 'var(--surface)' : 'transparent' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#f59e0b', flexShrink: 0 }} />
                            <span style={{ fontWeight: 600 }}>@{entry.target_username}</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ fontSize: 9, color: 'var(--text-muted)' }}>{entry.source}</span>
                            <button onClick={() => removeFromUnfollowList(entry.target_username)}
                              style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 2 }}>
                              <X size={12} />
                            </button>
                          </div>
                        </div>
                      ))}
                      {doneEntries.length > 0 && (
                        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed var(--border)' }}>
                          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Traités ({doneEntries.length})</div>
                          {doneEntries.slice(0, 20).map((entry) => (
                            <div key={entry.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '3px 8px', fontSize: 11, opacity: .5 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <Check size={10} style={{ color: '#22c55e' }} />
                                <span>@{entry.target_username}</span>
                              </div>
                              <button onClick={() => removeFromUnfollowList(entry.target_username)}
                                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 2 }}>
                                <X size={10} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </>)}
                  </div>
                  {/* Bulk add input */}
                  <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' }}>Ajout en masse (un username par ligne)</div>
                    <textarea value={bulkInput} onChange={e => setBulkInput(e.target.value)}
                      placeholder="username1&#10;username2&#10;username3"
                      style={{ width: '100%', height: 70, padding: 8, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-primary)', fontSize: 11, fontFamily: 'monospace', resize: 'none' }} />
                    <button onClick={() => {
                      const names = bulkInput.split('\n').map(l => l.trim().replace('@', '')).filter(Boolean);
                      if (names.length) addProfilesToUnfollowList(names, 'bulk');
                    }} disabled={!bulkInput.trim()}
                      style={{ marginTop: 6, width: '100%', padding: '7px 0', borderRadius: 8, border: 'none', background: bulkInput.trim() ? '#f59e0b' : 'var(--surface)', color: bulkInput.trim() ? '#fff' : 'var(--text-muted)', fontWeight: 700, fontSize: 11, cursor: bulkInput.trim() ? 'pointer' : 'default' }}>
                      <Plus size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />Ajouter {bulkInput.trim() ? `(${bulkInput.split('\n').filter(l => l.trim()).length})` : ''}
                    </button>
                  </div>
                </div>

                {/* Right: follow actions picker */}
                <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>Profils interagis</span>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {[
                        { key: 'follow', label: 'Follow', icon: <UserPlus size={10} /> },
                        { key: 'all', label: 'Tous', icon: <Users size={10} /> },
                        { key: 'like', label: 'Like', icon: <Heart size={10} /> },
                      ].map(f => (
                        <button key={f.key}
                          onClick={() => { setPickerFilter(f.key); fetchFollowedProfiles(f.key); }}
                          style={{
                            padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 3,
                            border: pickerFilter === f.key ? '1px solid var(--primary)' : '1px solid var(--border)',
                            background: pickerFilter === f.key ? 'rgba(165,145,239,.15)' : 'var(--surface)',
                            color: pickerFilter === f.key ? 'var(--primary)' : 'var(--text-muted)'
                          }}>{f.icon} {f.label}</button>
                      ))}
                    </div>
                    <input value={pickerSearch} onChange={e => setPickerSearch(e.target.value)}
                      placeholder="Rechercher..."
                      style={{ marginLeft: 'auto', padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-primary)', fontSize: 11, width: 120 }} />
                  </div>
                  {/* Select all */}
                  <div style={{ padding: '6px 16px', display: 'flex', alignItems: 'center', gap: 6, borderBottom: '1px solid var(--border)' }}>
                    <input type="checkbox" checked={allVisibleSelected}
                      onChange={() => {
                        if (allVisibleSelected) setSelectedProfiles(new Set());
                        else setSelectedProfiles(new Set(filtered.map(p => p.username)));
                      }}
                      style={{ accentColor: 'var(--primary)' }} />
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      {selectedProfiles.size > 0 ? `${selectedProfiles.size} sélectionné(s)` : `Tout sélectionner (${filtered.length})`}
                    </span>
                    {selectedProfiles.size > 0 && (
                      <button onClick={() => addProfilesToUnfollowList(Array.from(selectedProfiles), 'interaction')}
                        style={{ marginLeft: 'auto', padding: '4px 12px', borderRadius: 6, border: 'none', background: '#f59e0b', color: '#fff', fontWeight: 700, fontSize: 10, cursor: 'pointer' }}>
                        <Plus size={10} style={{ marginRight: 3, verticalAlign: 'middle' }} />Ajouter ({selectedProfiles.size})
                      </button>
                    )}
                  </div>
                  {/* Profiles list */}
                  <div style={{ flex: 1, overflowY: 'auto', maxHeight: 400 }}>
                    {profilesLoading ? (
                      <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)', fontSize: 12 }}>Chargement...</div>
                    ) : filtered.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)', fontSize: 12 }}>Aucun profil trouvé</div>
                    ) : filtered.slice(0, 500).map((p, i) => (
                      <div key={p.username} style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '6px 16px',
                        borderBottom: '1px solid var(--border)', cursor: 'pointer',
                        background: selectedProfiles.has(p.username) ? 'rgba(245,158,11,.08)' : (i % 2 === 0 ? 'transparent' : 'var(--surface)')
                      }} onClick={() => {
                        setSelectedProfiles(prev => {
                          const next = new Set(prev);
                          if (next.has(p.username)) next.delete(p.username); else next.add(p.username);
                          return next;
                        });
                      }}>
                        <input type="checkbox" checked={selectedProfiles.has(p.username)} readOnly
                          style={{ accentColor: '#f59e0b', flexShrink: 0 }} />
                        <span style={{ fontWeight: 600, fontSize: 12, flex: 1 }}>@{p.username}</span>
                        <div style={{ display: 'flex', gap: 4, fontSize: 10 }}>
                          {p.follow_count > 0 && <span style={{ padding: '1px 5px', borderRadius: 4, background: 'rgba(34,197,94,.1)', color: '#22c55e' }}>+{p.follow_count}</span>}
                          {p.like_count > 0 && <span style={{ padding: '1px 5px', borderRadius: 4, background: 'rgba(239,68,68,.1)', color: '#ef4444' }}>♥{p.like_count}</span>}
                          {p.story_count > 0 && <span style={{ padding: '1px 5px', borderRadius: 4, background: 'rgba(168,85,247,.1)', color: '#a855f7' }}>📖{p.story_count}</span>}
                          {p.currently_following && <span style={{ padding: '1px 5px', borderRadius: 4, background: 'rgba(59,130,246,.1)', color: '#3b82f6' }}>suivi</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div style={{ padding: '12px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={() => setShowUnfollowListModal(false)}
                  style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  Fermer
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </Page>
  );
};

export default AutomationDashboard;
